/**
 * TransformBar — Compact horizontal toolbar for point-and-click SQL operations.
 *
 * Sits between the Monaco SQL editor and the results table in the Data tab.
 * Each button opens a MUI Popover for configuring the operation.
 * All actions are pure TransformState mutations — the parent converts to SQL.
 *
 * Phase 9: Filter + Sort + Clear All + Undo
 * Phase 11 will add: Group By, Join, Add Column, Distinct
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Sort as SortIcon,
  GroupWork as GroupIcon,
  MergeType as JoinIcon,
  Rule as CaseIcon,
  AddCircleOutline as AddColIcon,
  FindReplace as FillMissingIcon,
  CalendarMonth as DateFormatIcon,
  Deblur as DistinctGlyphIcon,
  FindReplace as ReplaceIcon,
  SwapHoriz as CastColIcon,
  Edit as RenameColIcon,
  RemoveCircleOutline as DropColIcon,
  Deblur as DistinctIcon,
  Close as ClearIcon,
  Close as PopoverDismissIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  CallSplit as SplitIcon,
  Merge as MergeIcon,
  Search as LookupIcon,
  Analytics as WindowIcon,
  ArrowDropDown as ChevronIcon,
  ViewStream as UnionIcon,
} from '@mui/icons-material';
import type {
  TransformState,
  FilterClause,
  FilterOperator,
  SortDir,
  OrderByClause,
  AggregateFunc,
  AggregationClause,
  JoinClause,
  JoinCondition,
  JoinType,
  UnionAlignMode,
  HavingClause,
  HavingOperator,
  ReplaceMissingStrategy,
  LookupClause,
  CaseWhenClause,
  WindowFunctionClause,
  WindowFunctionName,
} from './sqlBuilder';
import {
  applyFilter,
  updateFilter,
  removeFilter,
  applySort,
  removeSort,
  applyGroupBy,
  clearGroupBy,
  applyHaving,
  removeHaving,
  applyJoin,
  removeJoin,
  applyUnion,
  removeUnion,
  setUnionDeduplicate,
  setUnionAlignMode,
  normalizeUnionAlignMode,
  applyLookup,
  removeLookup,
  applyAddColumn,
  applyCaseExpression,
  applyRename,
  applyDropColumn,
  applyCast,
  removeSelectColumn,
  applyReplaceMissing,
  applyDistinct,
  applySplitColumn,
  applyMergeColumns,
  applyDateFormat,
  applyWindowFunction,

  removeWindowFunction,
  applyReplacePattern,
  initStateFromTable,
  isStateModified,
  getBrokenTransformKinds,
  clearTransformKind,
  quoteIdent,
} from './sqlBuilder';
import type { TransformChipKind } from './sqlBuilder';

import { previewWorkspaceTable } from '../../services/workspaceApi';

// ── Types ────────────────────────────────────────────────────────────

export interface TransformBarProps {
  transformState: TransformState;
  onStateChange: (newState: TransformState) => void;
  columns: string[];
  columnTypes: Array<{ name: string; type: string }>;
  columnNullStats?: Record<string, { nullCount: number; total: number; pct: number }>;
  workspaceTables: Array<{ name: string; columns: Array<{ name: string; type: string }> }>;
  disabled?: boolean;
  /** History stack for undo/redo. Parent manages this. */
  canUndo?: boolean;
  onUndo?: () => void;
  canRedo?: boolean;
  onRedo?: () => void;
  sessionId?: string;
  /** When set, opens the Fill nulls popover for this column (from column menu suggestion). */
  fillNullsRequest?: string | null;
  onFillNullsRequestHandled?: () => void;
}

// ── Filter Operators ─────────────────────────────────────────────────

const FILTER_OPERATORS: Array<{ value: FilterOperator; label: string; needsValue: boolean }> = [
  { value: '=', label: '=', needsValue: true },
  { value: '!=', label: '≠', needsValue: true },
  { value: '>', label: '>', needsValue: true },
  { value: '<', label: '<', needsValue: true },
  { value: '>=', label: '≥', needsValue: true },
  { value: '<=', label: '≤', needsValue: true },
  { value: 'LIKE', label: 'LIKE', needsValue: true },
  { value: 'NOT LIKE', label: 'NOT LIKE', needsValue: true },
  { value: 'IN', label: 'IN', needsValue: true },
  { value: 'IS NULL', label: 'IS NULL', needsValue: false },
  { value: 'IS NOT NULL', label: 'IS NOT NULL', needsValue: false },
];

const REPLACE_MISSING_STRATEGIES: Array<{ value: ReplaceMissingStrategy; label: string }> = [
  { value: 'constant', label: 'Constant value' },
  { value: 'forward_fill', label: 'Forward fill' },
  { value: 'mean', label: 'Mean (numeric)' },
  { value: 'median', label: 'Median (numeric)' },
  { value: 'mode', label: 'Mode (numeric)' },
];

const CUSTOM_DATE_FORMAT_VALUE = '__custom__';

const DATE_FORMAT_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'YYYY-MM-DD', value: '%Y-%m-%d' },
  { label: 'MM/DD/YYYY', value: '%m/%d/%Y' },
  { label: 'DD-Mon-YYYY', value: '%d-%b-%Y' },
  { label: 'YYYY-MM-DD HH:mm:ss', value: '%Y-%m-%d %H:%M:%S' },
  { label: 'Mon DD, YYYY', value: '%b %d, %Y' },
  { label: 'Custom', value: CUSTOM_DATE_FORMAT_VALUE },
];
const CAST_TARGET_TYPES = ['VARCHAR', 'INTEGER', 'BIGINT', 'DOUBLE', 'DATE', 'TIMESTAMP', 'BOOLEAN'] as const;

function unescapeSqlStringLiteral(value: string): string {
  return value.replace(/''/g, "'");
}

function formatReplacePatternChipLabel(alias: string, expression: string): string {
  const regexpMatch = expression.match(/^REGEXP_REPLACE\([^,]+,\s*'((?:''|[^'])*)',\s*'((?:''|[^'])*)'/i);
  if (regexpMatch) {
    const find = unescapeSqlStringLiteral(regexpMatch[1]);
    const replace = unescapeSqlStringLiteral(regexpMatch[2]);
    return `${alias}: ${find} → ${replace}`;
  }
  const replaceMatch = expression.match(/^REPLACE\([^,]+,\s*'((?:''|[^'])*)',\s*'((?:''|[^'])*)'\)/i);
  if (replaceMatch) {
    const find = unescapeSqlStringLiteral(replaceMatch[1]);
    const replace = unescapeSqlStringLiteral(replaceMatch[2]);
    return `${alias}: ${find} → ${replace}`;
  }
  return alias;
}

const TransformPopoverHeader: React.FC<{ title: React.ReactNode; onClose: () => void }> = ({ title, onClose }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 1,
      mb: 1.5,
    }}
  >
    <Typography variant="subtitle2" sx={{ flex: 1, pr: 0.5, lineHeight: 1.35 }}>
      {title}
    </Typography>
    <Tooltip title="Close">
      <IconButton
        size="small"
        aria-label="Close"
        onClick={onClose}
        sx={{ flexShrink: 0, mt: -0.5, mr: -0.5 }}
      >
        <PopoverDismissIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  </Box>
);

function normalizeType(typeStr: string): string {
  const lower = typeStr.toLowerCase();
  if (/int|bigint|smallint|tinyint/.test(lower)) return 'numeric';
  if (/double|float|decimal|numeric/.test(lower)) return 'numeric';
  if (/varchar|text|string|char/.test(lower)) return 'string';
  if (/bool|boolean/.test(lower)) return 'boolean';
  if (/date|timestamp|time/.test(lower)) return 'temporal';
  return 'unknown';
}

function typesCompatibleForJoin(leftType: string, rightType: string): boolean {
  const left = normalizeType(leftType);
  const right = normalizeType(rightType);
  if (left === 'unknown' || right === 'unknown') return true;
  return left === right;
}

// ── Component ────────────────────────────────────────────────────────

const TransformBar: React.FC<TransformBarProps> = ({
  transformState,
  onStateChange,
  columns,
  columnTypes,
  columnNullStats,
  workspaceTables,
  disabled,
  canUndo,
  onUndo,
  canRedo,
  onRedo,
  sessionId,
  fillNullsRequest,
  onFillNullsRequestHandled,
}) => {
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  // Popover anchors
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);
  const [groupByAnchor, setGroupByAnchor] = useState<HTMLElement | null>(null);
  const [joinAnchor, setJoinAnchor] = useState<HTMLElement | null>(null);
  const [caseAnchor, setCaseAnchor] = useState<HTMLElement | null>(null);
  const [addColAnchor, setAddColAnchor] = useState<HTMLElement | null>(null);
  const [replaceMissingAnchor, setReplaceMissingAnchor] = useState<HTMLElement | null>(null);
  const [dateFormatAnchor, setDateFormatAnchor] = useState<HTMLElement | null>(null);
  const [windowAnchor, setWindowAnchor] = useState<HTMLElement | null>(null);
  const [replaceAnchor, setReplaceAnchor] = useState<HTMLElement | null>(null);
  const [castAnchor, setCastAnchor] = useState<HTMLElement | null>(null);
  const [renameAnchor, setRenameAnchor] = useState<HTMLElement | null>(null);
  const [dropAnchor, setDropAnchor] = useState<HTMLElement | null>(null);

  // Grouped dropdown menu anchors
  const [filterSortMenuAnchor, setFilterSortMenuAnchor] = useState<HTMLElement | null>(null);
  const [combineMenuAnchor, setCombineMenuAnchor] = useState<HTMLElement | null>(null);
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<HTMLElement | null>(null);
  const [transformMenuAnchor, setTransformMenuAnchor] = useState<HTMLElement | null>(null);

  // Filter form state
  const [filterColumn, setFilterColumn] = useState('');
  const [filterOperator, setFilterOperator] = useState<FilterOperator>('=');
  const [filterValue, setFilterValue] = useState('');
  const [editingFilterId, setEditingFilterId] = useState<string | null>(null);
  const [pendingFilterLogicOp, setPendingFilterLogicOp] = useState<'AND' | 'OR'>('AND');

  // Sort form state
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState<SortDir>('ASC');

  // Group By form state
  const [gbColumns, setGbColumns] = useState<string[]>([]);
  const [gbAggs, setGbAggs] = useState<AggregationClause[]>([]);
  const [gbAggCol, setGbAggCol] = useState('');
  const [gbAggFunc, setGbAggFunc] = useState<AggregateFunc>('COUNT');
  // HAVING form state
  const [gbHaving, setGbHaving] = useState<HavingClause[]>([]);
  const [gbHavingAggFunc, setGbHavingAggFunc] = useState<AggregateFunc | 'COUNT_STAR'>('COUNT_STAR');
  const [gbHavingAggCol, setGbHavingAggCol] = useState('');
  const [gbHavingOp, setGbHavingOp] = useState<HavingOperator>('>');
  const [gbHavingVal, setGbHavingVal] = useState('');

  // Join form state
  const [joinTable, setJoinTable] = useState('');
  const [joinType, setJoinType] = useState<JoinType>('INNER');
  const [joinConditions, setJoinConditions] = useState<JoinCondition[]>([{ leftColumn: '', rightColumn: '' }]);
  const [joinColumns, setJoinColumns] = useState<string[]>([]);

  // Lookup form state
  const [lookupAnchor, setLookupAnchor] = useState<HTMLElement | null>(null);
  const [lookupTable, setLookupTable] = useState('');
  const [lookupSourceKey, setLookupSourceKey] = useState('');
  const [lookupRefKey, setLookupRefKey] = useState('');
  const [lookupColumns, setLookupColumns] = useState<string[]>([]);
  const [lookupKeepUnmatched, setLookupKeepUnmatched] = useState(true);
  const [lookupMatchBehavior, setLookupMatchBehavior] = useState<'first' | 'all'>('first');
  const [lookupIsUnique, setLookupIsUnique] = useState(false);
  const [lookupPreview, setLookupPreview] = useState<any>(null);
  const [lookupPreviewLoading, setLookupPreviewLoading] = useState(false);

  // Add Column form state
  const [addColExpr, setAddColExpr] = useState('');
  const [addColAlias, setAddColAlias] = useState('');

  // Replace Missing form state
  const [replaceMissingColumns, setReplaceMissingColumns] = useState<string[]>([]);
  const [replaceMissingStrategy, setReplaceMissingStrategy] = useState<ReplaceMissingStrategy>('constant');
  const [replaceMissingValue, setReplaceMissingValue] = useState('');

  // Date Format form state
  const [dateFormatColumn, setDateFormatColumn] = useState('');
  const [dateFormatPreset, setDateFormatPreset] = useState(DATE_FORMAT_OPTIONS[0].value);
  const [dateFormatCustom, setDateFormatCustom] = useState('');
  const [dateInputFormat, setDateInputFormat] = useState('');

  // Replace form state
  const [replaceColumn, setReplaceColumn] = useState('');
  const [replaceFindValue, setReplaceFindValue] = useState('');
  const [replaceWithValue, setReplaceWithValue] = useState('');
  const [replaceUseRegex, setReplaceUseRegex] = useState(false);
  const [replaceCaseSensitive, setReplaceCaseSensitive] = useState(true);
  const [replaceWholeWord, setReplaceWholeWord] = useState(false);

  // CASE / IF form state
  const [caseOutputName, setCaseOutputName] = useState('');
  const [caseElseValue, setCaseElseValue] = useState('NULL');
  const [caseClauses, setCaseClauses] = useState<CaseWhenClause[]>([]);

  // Rename Column form state
  const [renameColumn, setRenameColumn] = useState('');
  const [renameNewName, setRenameNewName] = useState('');

  // Cast Column form state
  const [castColumn, setCastColumn] = useState('');
  const [castTargetType, setCastTargetType] = useState<(typeof CAST_TARGET_TYPES)[number]>('VARCHAR');

  // Drop Column form state
  const [dropColumns, setDropColumns] = useState<string[]>([]);

  // Split form state
  const [splitAnchor, setSplitAnchor] = useState<HTMLElement | null>(null);
  const [splitColumn, setSplitColumn] = useState('');
  const [splitDelimiter, setSplitDelimiter] = useState(',');
  const [splitCount, setSplitCount] = useState(2);

  // Merge form state
  const [mergeAnchor, setMergeAnchor] = useState<HTMLElement | null>(null);
  const [mergeColumns, setMergeColumns] = useState<string[]>([]);
  const [mergeSeparator, setMergeSeparator] = useState(' ');
  const [mergeAlias, setMergeAlias] = useState('');
  const [mergeDropSources, setMergeDropSources] = useState(false);

  // Union form state
  const [unionAnchor, setUnionAnchor] = useState<HTMLElement | null>(null);
  const [unionTable, setUnionTable] = useState('');
  const [unionColumns, setUnionColumns] = useState<string[]>([]);
  const [unionDeduplicate, setUnionDeduplicateLocal] = useState(false);
  const [unionAlignMode, setUnionAlignModeLocal] = useState<UnionAlignMode>('auto');
  const [windowOutputCol, setWindowOutputCol] = useState('');
  const [windowFunc, setWindowFunc] = useState<WindowFunctionName>('ROW_NUMBER');
  const [windowValueCol, setWindowValueCol] = useState('');
  const [windowNtileBuckets, setWindowNtileBuckets] = useState('4');
  const [windowPartitionBy, setWindowPartitionBy] = useState<string[]>([]);
  const [windowOrderCol, setWindowOrderCol] = useState('');
  const [windowOrderDir, setWindowOrderDir] = useState<SortDir>('ASC');
  const [windowFrame, setWindowFrame] = useState('');
  const [windowAdvancedOpen, setWindowAdvancedOpen] = useState(false);

  // Filter negate
  const [filterNegate, setFilterNegate] = useState(false);

  const modified = isStateModified(transformState);

  // Derived: other tables for join (exclude base table)
  const otherTables = useMemo(
    () => workspaceTables.filter((t) => t.name !== transformState.baseTable),
    [workspaceTables, transformState.baseTable],
  );

  // Columns of the join table
  const joinTableColumns = useMemo(
    () => workspaceTables.find((t) => t.name === joinTable)?.columns || [],
    [workspaceTables, joinTable],
  );

  const unionTableColumns = useMemo(
    () => workspaceTables.find((t) => t.name === unionTable)?.columns || [],
    [workspaceTables, unionTable],
  );

  const unionSelectableTables = useMemo(() => {
    const stacked = new Set(transformState.unions.map((u) => u.table));
    return otherTables.filter((t) => !stacked.has(t.name));
  }, [otherTables, transformState.unions]);

  const unionTableOptions = useMemo(() => {
    const stacked = transformState.unions.map((u) => u.table);
    const available = unionSelectableTables.map((t) => t.name);
    return [...stacked, ...available];
  }, [transformState.unions, unionSelectableTables]);

  const numericColumns = useMemo(
    () => columnTypes.filter((c) => /int|bigint|double|float|decimal|numeric|smallint|tinyint/i.test(c.type)).map((c) => c.name),
    [columnTypes],
  );

  const joinConditionErrors = useMemo(() => {
    const typesByName = new Map(columnTypes.map((c) => [c.name, c.type]));
    const joinTableColTypes = new Map(joinTableColumns.map((c) => [c.name, c.type]));

    return joinConditions.map((cond, idx) => {
      if (!cond.leftColumn || !cond.rightColumn) return null;
      const leftType = typesByName.get(cond.leftColumn);
      const rightType = joinTableColTypes.get(cond.rightColumn);
      if (!leftType || !rightType) return null;
      if (!typesCompatibleForJoin(leftType, rightType)) {
        return `Column "${cond.leftColumn}" (${leftType}) is not compatible with "${cond.rightColumn}" (${rightType})`;
      }
      return null;
    });
  }, [joinConditions, columnTypes, joinTableColumns]);

  const hasJoinErrors = joinConditionErrors.some((err) => err !== null);

  const splitSelectableColumns = useMemo(() => columns, [columns]);

  const replaceMissingSelectableColumns = useMemo(() => {
    const candidateColumns = replaceMissingStrategy === 'mean' || replaceMissingStrategy === 'median' || replaceMissingStrategy === 'mode'
      ? (() => {
        const numericColumnNames = new Set(numericColumns);
        return columns.filter((col) => numericColumnNames.has(col));
      })()
      : columns;

    return candidateColumns;
  }, [replaceMissingStrategy, numericColumns, columns]);

  const replaceMissingApplicableColumns = useMemo(
    () => {
      const selectableColumnNames = new Set(replaceMissingSelectableColumns);
      return replaceMissingColumns.filter((column) => selectableColumnNames.has(column));
    },
    [replaceMissingColumns, replaceMissingSelectableColumns],
  );
  /** Columns that still need a fill per the latest query stats (omit confirmed zero-NULL columns). */
  const replaceMissingEffectiveColumns = useMemo(() => {
    if (!columnNullStats || Object.keys(columnNullStats).length === 0) {
      return replaceMissingApplicableColumns;
    }
    return replaceMissingApplicableColumns.filter((col) => {
      const stats = columnNullStats[col];
      if (!stats) return true;
      return stats.nullCount > 0;
    });
  }, [replaceMissingApplicableColumns, columnNullStats]);
  const replaceMissingColumnTypeMap = useMemo(
    () => Object.fromEntries(columnTypes.map((column) => [column.name, column.type])),
    [columnTypes],
  );
  const replaceMissingNeedsExplicitValue = useMemo(
    () => replaceMissingStrategy === 'constant' && replaceMissingEffectiveColumns.some((column) => {
      const columnType = replaceMissingColumnTypeMap[column];
      return !!columnType && !/varchar|text|string|char/i.test(columnType);
    }),
    [replaceMissingStrategy, replaceMissingEffectiveColumns, replaceMissingColumnTypeMap],
  );
  const canApplyReplaceMissing = useMemo(
    () => replaceMissingEffectiveColumns.length > 0
      && (!replaceMissingNeedsExplicitValue || replaceMissingValue.trim().length > 0),
    [replaceMissingEffectiveColumns, replaceMissingNeedsExplicitValue, replaceMissingValue],
  );
  const replaceMissingNoOpDueToStats = useMemo(
    () => replaceMissingApplicableColumns.length > 0
      && replaceMissingEffectiveColumns.length === 0
      && !!columnNullStats
      && Object.keys(columnNullStats).length > 0,
    [replaceMissingApplicableColumns, replaceMissingEffectiveColumns, columnNullStats],
  );

  const dateFormatSelectableColumns = useMemo(() => {
    const typeByColumn = new Map(columnTypes.map((column) => [column.name, column.type]));

    return columns.filter((column) => {
      const dtype = typeByColumn.get(column) || '';
      if (/date|timestamp|datetime/i.test(dtype)) return true;
      return /varchar|text|string|char/i.test(dtype) && /(?:^|_)(date|dt|time|timestamp)(?:_|$)/i.test(column);
    });
  }, [columnTypes, columns]);

  const replaceSelectableColumns = useMemo(() => columns, [columns]);

  // Helper to detect if operator needs a value
  const needsValue = useMemo(
    () => FILTER_OPERATORS.find((o) => o.value === filterOperator)?.needsValue ?? true,
    [filterOperator],
  );

  const addedColumns = useMemo(
    () => transformState.selectColumns.filter(
      (column) => column.alias
        && column.expression !== '*'
        && !column.isReplacement
        && !column.isRename
        && column.expression !== quoteIdent(column.alias),
    ),
    [transformState.selectColumns],
  );

  /** Exclude CASE expressions — they use the separate "case" toolbar chip. */
  const addedColumnsForToolbarBadge = useMemo(
    () => addedColumns.filter((column) => !/^CASE\s+WHEN\b/i.test(column.expression)),
    [addedColumns],
  );

  const replaceMissingColumnsActive = useMemo(
    () => transformState.selectColumns.filter((column) => /^COALESCE\(/.test(column.expression)),
    [transformState.selectColumns],
  );

  const replacePatternColumnsActive = useMemo(
    () => transformState.selectColumns.filter(
      (column) => column.isReplacement && (
        /^REPLACE\(/i.test(column.expression) ||
        /^REGEXP_REPLACE\(/i.test(column.expression)
      ),
    ),
    [transformState.selectColumns],
  );
  const windowFunctions = transformState.windowFunctions ?? [];
  const valueColumnOptionalFunctions = useMemo(
    () => new Set<WindowFunctionName>(['COUNT', 'RUNNING_COUNT']),
    [],
  );
  const rankingFunctions = useMemo(
    () => new Set<WindowFunctionName>(['ROW_NUMBER', 'RANK', 'DENSE_RANK', 'NTILE']),
    [],
  );
  const aggregateFunctions = useMemo(
    () => new Set<WindowFunctionName>(['SUM', 'AVG', 'COUNT', 'MIN', 'MAX', 'RUNNING_SUM', 'RUNNING_COUNT']),
    [],
  );
  const navigationFunctions = useMemo(
    () => new Set<WindowFunctionName>(['LAG', 'LEAD', 'FIRST_VALUE', 'LAST_VALUE']),
    [],
  );
  const needsOrderForWindow = useMemo(
    () => new Set<WindowFunctionName>(['ROW_NUMBER', 'RANK', 'DENSE_RANK', 'NTILE', 'LAG', 'LEAD', 'FIRST_VALUE', 'LAST_VALUE', 'RUNNING_SUM', 'RUNNING_COUNT']),
    [],
  );
  const needsValueForWindow = useMemo(
    () => new Set<WindowFunctionName>(['SUM', 'AVG', 'MIN', 'MAX', 'LAG', 'LEAD', 'FIRST_VALUE', 'LAST_VALUE', 'RUNNING_SUM']),
    [],
  );
  const columnTypeByName = useMemo(() => {
    return new Map(columnTypes.map((column) => [column.name, column.type.toLowerCase()]));
  }, [columnTypes]);
  const isNumericType = useCallback((columnType?: string) => {
    if (!columnType) return false;
    return /(int|bigint|smallint|tinyint|decimal|numeric|double|float|real|money)/i.test(columnType);
  }, []);
  const windowTypeValidationError = useMemo(() => {
    if (!windowValueCol) return '';
    const selectedType = columnTypeByName.get(windowValueCol);
    if (!selectedType) return '';
    if ((windowFunc === 'SUM' || windowFunc === 'AVG' || windowFunc === 'RUNNING_SUM') && !isNumericType(selectedType)) {
      return `${windowFunc} requires a numeric value column. "${windowValueCol}" is ${selectedType}.`;
    }
    return '';
  }, [windowFunc, windowValueCol, columnTypeByName, isNumericType]);
  const ntileValidationError = useMemo(() => {
    if (windowFunc !== 'NTILE') return '';
    const buckets = Number(windowNtileBuckets);
    if (!Number.isInteger(buckets) || buckets <= 0) {
      return 'NTILE requires a positive integer bucket count.';
    }
    return '';
  }, [windowFunc, windowNtileBuckets]);

  const renamedColumns = useMemo(
    () => transformState.selectColumns.filter((column) => column.isRename),
    [transformState.selectColumns],
  );

  const castedColumns = useMemo(
    () => transformState.selectColumns.filter(
      (column) => column.isReplacement && (
        !!column.outputType ||
        /^TRY_CAST\(/i.test(column.expression) ||
        /^try_strptime\(/i.test(column.expression)
      ),
    ),
    [transformState.selectColumns],
  );

  const brokenTransformKinds = useMemo(
    () => getBrokenTransformKinds(transformState),
    [transformState],
  );

  const toolbarChipColor = useCallback((
    kind: TransformChipKind,
    defaultColor?: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'default',
  ) => (brokenTransformKinds.has(kind) ? 'error' : defaultColor), [brokenTransformKinds]);

  const handleRemoveTransformChip = useCallback((kind: TransformChipKind) => {
    onStateChange(clearTransformKind(transformState, kind));
  }, [transformState, onStateChange]);

  const caseExpressions = useMemo(
    () => transformState.selectColumns.filter((column) => /^CASE\s+WHEN\b/i.test(column.expression)),
    [transformState.selectColumns],
  );

  const renameError = useMemo(() => {
    const trimmed = renameNewName.trim();
    if (!trimmed) return '';
    if (!renameColumn) return 'Select a column to rename';
    if (trimmed === renameColumn) return 'Enter a different name';
    if (columns.some((column) => column === trimmed && column !== renameColumn)) {
      return 'A column with this name already exists';
    }
    return '';
  }, [renameNewName, renameColumn, columns]);

  const caseHasInvalidClause = useMemo(
    () => caseClauses.some((clause) => {
      const needsCaseValue = FILTER_OPERATORS.find((operator) => operator.value === clause.operator)?.needsValue ?? true;
      return !clause.column || !clause.result.trim() || (needsCaseValue && !clause.value.trim());
    }),
    [caseClauses],
  );

  // ── Filter Handlers ──────────────────────────────────────────────

  const handleOpenFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setFilterColumn(columns[0] || '');
    setFilterOperator('=');
    setFilterValue('');
    setFilterNegate(false);
    setEditingFilterId(null);
    setPendingFilterLogicOp('AND');
    setFilterAnchor(e.currentTarget);
  }, [columns]);

  const handleEditFilter = useCallback((f: FilterClause, e: React.MouseEvent) => {
    e.stopPropagation();
    setFilterColumn(f.column);
    setFilterOperator(f.operator);
    setFilterValue(f.value);
    setFilterNegate(f.negate || false);
    setEditingFilterId(f.id);
    setPendingFilterLogicOp(f.logicOp || 'AND');
  }, []);

  const handleApplyFilter = useCallback(() => {
    if (!filterColumn) return;
    if (needsValue && !filterValue.trim() && filterOperator !== 'IS NULL' && filterOperator !== 'IS NOT NULL') return;
    if (editingFilterId) {
      onStateChange(updateFilter(transformState, editingFilterId, {
        column: filterColumn,
        operator: filterOperator,
        value: filterValue,
        negate: filterNegate,
        logicOp: pendingFilterLogicOp,
      }));
    } else {
      const isFirst = transformState.filters.length === 0;
      onStateChange(applyFilter(transformState, {
        column: filterColumn,
        operator: filterOperator,
        value: filterValue,
        negate: filterNegate,
        logicOp: isFirst ? undefined : pendingFilterLogicOp,
      }));
    }
    setEditingFilterId(null);
    setFilterAnchor(null);
  }, [transformState, onStateChange, filterColumn, filterOperator, filterValue, needsValue, filterNegate, editingFilterId, pendingFilterLogicOp]);

  const handleRemoveFilter = useCallback((filterId: string) => {
    onStateChange(removeFilter(transformState, filterId));
  }, [transformState, onStateChange]);

  // ── Sort Handlers ────────────────────────────────────────────────

  const handleOpenSort = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setSortColumn(columns[0] || '');
    setSortDirection('ASC');
    setSortAnchor(e.currentTarget);
  }, [columns]);

  const handleApplySort = useCallback(() => {
    if (!sortColumn) return;
    onStateChange(applySort(transformState, sortColumn, sortDirection));
    setSortAnchor(null);
  }, [transformState, onStateChange, sortColumn, sortDirection]);

  const handleRemoveSort = useCallback((col: string) => {
    onStateChange(removeSort(transformState, col));
  }, [transformState, onStateChange]);

  // ── Group By Handlers ────────────────────────────────────────────

  const handleOpenGroupBy = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setGbColumns(transformState.groupByColumns);
    setGbAggs(transformState.aggregations);
    setGbHaving(transformState.having || []);
    setGbAggCol('');
    setGbAggFunc('COUNT');
    setGbHavingAggFunc('COUNT_STAR');
    setGbHavingAggCol(columns[0] || '');
    setGbHavingOp('>');
    setGbHavingVal('');
    setGroupByAnchor(e.currentTarget);
  }, [columns, transformState.groupByColumns, transformState.aggregations, transformState.having]);

  const handleAddAgg = useCallback(() => {
    if (!gbAggCol) return;
    const alias = `${gbAggFunc.toLowerCase()}_${gbAggCol}`;
    setGbAggs((prev) => [...prev, { column: gbAggCol, func: gbAggFunc, alias }]);
    setGbAggCol('');
  }, [gbAggCol, gbAggFunc]);

  const handleAddHaving = useCallback(() => {
    if (!gbHavingVal.trim()) return;
    const id = `h_local_${Date.now()}`;
    setGbHaving((prev) => [...prev, {
      id,
      aggregateFunc: gbHavingAggFunc,
      column: gbHavingAggCol,
      operator: gbHavingOp,
      value: gbHavingVal,
    }]);
    setGbHavingVal('');
  }, [gbHavingAggFunc, gbHavingAggCol, gbHavingOp, gbHavingVal]);

  const handleApplyGroupBy = useCallback(() => {
    let aggsToApply = gbAggs;
    if (gbAggCol) {
      const alreadyListed = gbAggs.some((agg) => agg.column === gbAggCol && agg.func === gbAggFunc);
      if (!alreadyListed) {
        aggsToApply = [
          ...gbAggs,
          { column: gbAggCol, func: gbAggFunc, alias: `${gbAggFunc.toLowerCase()}_${gbAggCol}` },
        ];
      }
    }

    if (gbColumns.length === 0 && aggsToApply.length === 0 && gbHaving.length === 0) {
      onStateChange(clearGroupBy(transformState));
    } else {
      onStateChange(applyGroupBy(transformState, gbColumns, aggsToApply, gbHaving));
    }
    setGroupByAnchor(null);
  }, [transformState, onStateChange, gbColumns, gbAggs, gbAggCol, gbAggFunc, gbHaving]);

  // ── Join Handlers ────────────────────────────────────────────────

  const handleOpenJoin = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setJoinTable(otherTables[0]?.name || '');
    setJoinType('INNER');
    setJoinConditions([{ leftColumn: columns[0] || '', rightColumn: '' }]);
    setJoinColumns([]);
    setJoinAnchor(e.currentTarget);
  }, [otherTables, columns]);

  const handleApplyJoin = useCallback(() => {
    const validConditions = joinConditions.filter((c) => c.leftColumn && c.rightColumn);
    if (!joinTable || validConditions.length === 0) return;
    onStateChange(applyJoin(transformState, {
      table: joinTable,
      joinType,
      conditions: validConditions,
      columns: joinColumns,
    }));
    setJoinAnchor(null);
  }, [transformState, onStateChange, joinTable, joinType, joinConditions, joinColumns]);

  // ── CASE / IF Handlers ───────────────────────────────────────────

  const handleOpenCase = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setCaseOutputName('');
    setCaseElseValue('NULL');
    setCaseClauses([{ column: columns[0] || '', operator: '=', value: '', result: '' }]);
    setCaseAnchor(e.currentTarget);
  }, [columns]);

  const handleAddCaseClause = useCallback(() => {
    setCaseClauses((prev) => [...prev, { column: columns[0] || '', operator: '=', value: '', result: '' }]);
  }, [columns]);

  const handleApplyCase = useCallback(() => {
    if (!caseOutputName.trim() || !caseElseValue.trim() || caseClauses.length === 0 || caseHasInvalidClause) return;
    onStateChange(applyCaseExpression(
      transformState,
      caseOutputName.trim(),
      caseClauses.map((clause) => ({
        ...clause,
        value: clause.value.trim(),
        result: clause.result.trim(),
      })),
      caseElseValue.trim(),
      columns,
    ));
    setCaseAnchor(null);
  }, [caseOutputName, caseElseValue, caseClauses, caseHasInvalidClause, onStateChange, transformState, columns]);

  // ── Add Column Handlers ──────────────────────────────────────────

  const handleOpenAddCol = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAddColExpr('');
    setAddColAlias('');
    setAddColAnchor(e.currentTarget);
  }, []);

  const handleApplyAddCol = useCallback(() => {
    if (!addColExpr.trim() || !addColAlias.trim()) return;
    onStateChange(applyAddColumn(transformState, addColExpr, addColAlias, columns));
    setAddColAnchor(null);
  }, [transformState, onStateChange, addColExpr, addColAlias, columns]);

  const handleRemoveAddedColumn = useCallback((alias: string) => {
    onStateChange(removeSelectColumn(transformState, alias));
  }, [transformState, onStateChange]);

  // ── Replace Missing Handlers ─────────────────────────────────────

  const handleOpenReplaceMissing = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setReplaceMissingStrategy('constant');
    setReplaceMissingValue('');
    setReplaceMissingColumns([]);
    setReplaceMissingAnchor(e.currentTarget);
  }, []);

  useEffect(() => {
    if (!fillNullsRequest || !toolbarRef.current) return;
    setReplaceMissingStrategy('constant');
    setReplaceMissingValue('');
    setReplaceMissingColumns([fillNullsRequest]);
    setReplaceMissingAnchor(toolbarRef.current);
    onFillNullsRequestHandled?.();
  }, [fillNullsRequest, onFillNullsRequestHandled]);

  const handleApplyReplaceMissing = useCallback(() => {
    if (!canApplyReplaceMissing) return;
    onStateChange(applyReplaceMissing(
      transformState,
      replaceMissingEffectiveColumns,
      replaceMissingStrategy,
      replaceMissingValue,
      replaceMissingColumnTypeMap,
    ));
    setReplaceMissingAnchor(null);
  }, [transformState, onStateChange, replaceMissingEffectiveColumns, replaceMissingStrategy, replaceMissingValue, replaceMissingColumnTypeMap, canApplyReplaceMissing]);

  const handleOpenDateFormat = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setDateFormatColumn(dateFormatSelectableColumns[0] || '');
    setDateFormatPreset(DATE_FORMAT_OPTIONS[0].value);
    setDateFormatCustom('');
    setDateInputFormat('');
    setDateFormatAnchor(e.currentTarget);
  }, [dateFormatSelectableColumns]);

  const handleApplyDateFormat = useCallback(() => {
    const outputFormat = dateFormatPreset === CUSTOM_DATE_FORMAT_VALUE
      ? dateFormatCustom.trim()
      : dateFormatPreset;
    if (!dateFormatColumn || !outputFormat) return;
    onStateChange(applyDateFormat(
      transformState,
      dateFormatColumn,
      outputFormat,
      dateInputFormat.trim() || undefined,
    ));
    setDateFormatAnchor(null);
  }, [transformState, onStateChange, dateFormatColumn, dateFormatPreset, dateFormatCustom, dateInputFormat]);

  const handleOpenReplace = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setReplaceColumn(replaceSelectableColumns[0] || columns[0] || '');
    setReplaceFindValue('');
    setReplaceWithValue('');
    setReplaceUseRegex(false);
    setReplaceCaseSensitive(true);
    setReplaceWholeWord(false);
    setReplaceAnchor(e.currentTarget);
  }, [replaceSelectableColumns, columns]);

  const handleApplyReplace = useCallback(() => {
    if (!replaceColumn || !replaceFindValue) return;
    onStateChange(applyReplacePattern(
      transformState,
      replaceColumn,
      replaceFindValue,
      replaceWithValue,
      {
        regex: replaceUseRegex,
        caseSensitive: replaceCaseSensitive,
        wholeWord: replaceWholeWord,
      },
    ));
    setReplaceAnchor(null);
  }, [
    transformState,
    onStateChange,
    replaceColumn,
    replaceFindValue,
    replaceWithValue,
    replaceUseRegex,
    replaceCaseSensitive,
    replaceWholeWord,
  ]);

  // ── Cast Column Handlers ────────────────────────────────────────

  const handleOpenCast = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setCastColumn(columns[0] || '');
    setCastTargetType('VARCHAR');
    setCastAnchor(e.currentTarget);
  }, [columns]);

  const handleApplyCast = useCallback(() => {
    if (!castColumn || !castTargetType) return;
    const sourceType = columnTypes.find((c) => c.name === castColumn)?.type;
    onStateChange(applyCast(transformState, castColumn, castTargetType, sourceType));
    setCastAnchor(null);
  }, [castColumn, castTargetType, columnTypes, onStateChange, transformState]);

  // ── Rename Column Handlers ──────────────────────────────────────

  const handleOpenRename = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const defaultColumn = columns[0] || '';
    setRenameColumn(defaultColumn);
    setRenameNewName(defaultColumn);
    setRenameAnchor(e.currentTarget);
  }, [columns]);

  const handleApplyRename = useCallback(() => {
    const trimmedName = renameNewName.trim();
    if (!renameColumn || !trimmedName || renameError) return;

    onStateChange(applyRename(transformState, renameColumn, trimmedName));
    setRenameAnchor(null);
  }, [renameColumn, renameNewName, renameError, onStateChange, transformState]);

  // ── Drop Column Handlers ────────────────────────────────────────

  const handleOpenDrop = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setDropColumns([]);
    setDropAnchor(e.currentTarget);
  }, []);

  const handleApplyDrop = useCallback(() => {
    if (dropColumns.length === 0) return;

    const uniqueColumns = Array.from(new Set(dropColumns));
    const nextState = uniqueColumns.reduce(
      (state, column) => applyDropColumn(state, column),
      transformState,
    );

    onStateChange(nextState);
    setDropAnchor(null);
    setDropColumns([]);
  }, [dropColumns, transformState, onStateChange]);

  // ── Split Handlers ───────────────────────────────────────────────

  const handleOpenSplit = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setSplitColumn(splitSelectableColumns[0] || columns[0] || '');
    setSplitDelimiter(',');
    setSplitCount(2);
    setSplitAnchor(e.currentTarget);
  }, [splitSelectableColumns, columns]);

  const handleApplySplit = useCallback(() => {
    if (!splitColumn || !splitDelimiter) return;
    onStateChange(applySplitColumn(transformState, splitColumn, splitDelimiter, splitCount));
    setSplitAnchor(null);
  }, [transformState, onStateChange, splitColumn, splitDelimiter, splitCount]);

  // ── Lookup Handlers ──────────────────────────────────────────────

  const fetchLookupPreview = useCallback(async (tableName: string) => {
    if (!tableName || !sessionId) return;
    setLookupPreviewLoading(true);
    try {
      const data = await previewWorkspaceTable(sessionId, tableName, 5);
      setLookupPreview(data);
      if (data.columns.length > 0) {
        setLookupRefKey(data.columns[0]);
      }
    } catch (err) {
      console.error('Failed to fetch lookup preview:', err);
    } finally {
      setLookupPreviewLoading(false);
    }
  }, [sessionId]);

  const handleOpenLookup = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const defaultTable = otherTables[0]?.name || '';
    setLookupTable(defaultTable);
    setLookupSourceKey(columns[0] || '');
    setLookupRefKey('');
    setLookupColumns([]);
    setLookupKeepUnmatched(true);
    setLookupMatchBehavior('first');
    setLookupIsUnique(false);
    setLookupPreview(null);
    setLookupAnchor(e.currentTarget);
    if (defaultTable) {
      fetchLookupPreview(defaultTable);
    }
  }, [otherTables, columns, fetchLookupPreview]);

  const handleApplyLookup = useCallback(() => {
    if (!lookupTable || !lookupSourceKey || !lookupRefKey || lookupColumns.length === 0) return;
    onStateChange(applyLookup(transformState, {
      refTable: lookupTable,
      sourceKey: lookupSourceKey,
      refKey: lookupRefKey,
      columns: lookupColumns,
      keepUnmatched: lookupKeepUnmatched,
      matchBehavior: lookupMatchBehavior,
      isUnique: lookupIsUnique,
    }));
    setLookupAnchor(null);
  }, [transformState, onStateChange, lookupTable, lookupSourceKey, lookupRefKey, lookupColumns, lookupKeepUnmatched, lookupMatchBehavior, lookupIsUnique]);

  const handleRemoveLookup = useCallback((lookupId: string) => {
    onStateChange(removeLookup(transformState, lookupId));
  }, [transformState, onStateChange]);

  // ── Merge Handlers ──────────────────────────────────────────────

  const handleOpenMerge = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setMergeColumns([]);
    setMergeSeparator('');
    setMergeAlias('');
    setMergeDropSources(false);
    setMergeAnchor(e.currentTarget);
  }, []);

  const handleApplyMerge = useCallback(() => {
    if (mergeColumns.length < 2 || !mergeAlias.trim()) return;
    onStateChange(applyMergeColumns(transformState, mergeColumns, mergeSeparator, mergeAlias, mergeDropSources));
    setMergeAnchor(null);
  }, [transformState, onStateChange, mergeColumns, mergeSeparator, mergeAlias, mergeDropSources]);

  // ── Union Handlers ──────────────────────────────────────────────

  const handleOpenUnion = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setUnionTable(
      transformState.unions.length > 0
        ? transformState.unions[transformState.unions.length - 1].table
        : (unionSelectableTables[0]?.name || ''),
    );
    setUnionColumns([]);
    setUnionDeduplicateLocal(transformState.unionDeduplicate);
    setUnionAlignModeLocal(normalizeUnionAlignMode(transformState.unionAlignMode));
    setUnionAnchor(e.currentTarget);
  }, [unionSelectableTables, transformState.unionDeduplicate, transformState.unionAlignMode, transformState.unions]);

  const handleApplyUnion = useCallback(() => {
    const schemaSnapshot = unionTableColumns.map((column) => column.name);
    const stackedTables = new Set(transformState.unions.map((u) => u.table));
    const canAddTable = Boolean(unionTable)
      && !stackedTables.has(unionTable)
      && unionTable !== transformState.baseTable;
    const settingsChanged = unionDeduplicate !== transformState.unionDeduplicate
      || unionAlignMode !== normalizeUnionAlignMode(transformState.unionAlignMode);

    if (!canAddTable && !settingsChanged) return;

    let nextState = transformState;

    if (canAddTable) {
      nextState = applyUnion(nextState, {
        table: unionTable,
        columns: unionColumns,
        schemaSnapshot,
      }, {
        deduplicate: unionDeduplicate,
        alignMode: unionAlignMode,
        schemaSnapshot,
      });
    }

    if (unionDeduplicate !== nextState.unionDeduplicate) {
      nextState = setUnionDeduplicate(nextState, unionDeduplicate);
    }
    if (normalizeUnionAlignMode(nextState.unionAlignMode) !== unionAlignMode) {
      nextState = setUnionAlignMode(nextState, unionAlignMode);
    }

    onStateChange(nextState);
    setUnionAnchor(null);
  }, [
    transformState,
    onStateChange,
    unionTable,
    unionColumns,
    unionDeduplicate,
    unionAlignMode,
    unionTableColumns,
  ]);

  const unionSettingsDirty = unionDeduplicate !== transformState.unionDeduplicate
    || unionAlignMode !== normalizeUnionAlignMode(transformState.unionAlignMode);
  const canAddUnionTable = Boolean(unionTable)
    && unionSelectableTables.some((table) => table.name === unionTable);
  const canApplyUnion = canAddUnionTable
    || (transformState.unions.length > 0 && unionSettingsDirty);
  const unionApplyLabel = canAddUnionTable && unionSettingsDirty
    ? 'Add Union & Apply Settings'
    : canAddUnionTable
      ? 'Add Union'
      : 'Apply Settings';

  // ── Window Function Handlers ───────────────────────────────────

  const handleOpenWindow = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setWindowOutputCol('');
    setWindowFunc('ROW_NUMBER');
    setWindowValueCol(columns[0] || '');
    setWindowNtileBuckets('4');
    setWindowPartitionBy([]);
    setWindowOrderCol('');
    setWindowOrderDir('ASC');
    setWindowFrame('');
    setWindowAdvancedOpen(false);
    setWindowAnchor(e.currentTarget);
  }, [columns]);

  const handleApplyWindow = useCallback(() => {
    const out = windowOutputCol.trim();
    const ntileBuckets = Number(windowNtileBuckets);
    if (!out) return;
    if (windowFunc === 'NTILE' && (!Number.isInteger(ntileBuckets) || ntileBuckets <= 0)) return;
    if (needsValueForWindow.has(windowFunc) && !windowValueCol) return;
    if (needsOrderForWindow.has(windowFunc) && !windowOrderCol) return;
    if (windowTypeValidationError || ntileValidationError) return;
    const clause: WindowFunctionClause = {
      outputCol: out,
      func: windowFunc,
      partitionBy: windowPartitionBy,
      ...(windowFunc === 'NTILE' ? { ntileBuckets } : {}),
      ...((needsValueForWindow.has(windowFunc) || valueColumnOptionalFunctions.has(windowFunc)) && windowValueCol
        ? { valueCol: windowValueCol }
        : {}),
      ...(windowOrderCol ? { orderBy: { col: windowOrderCol, dir: windowOrderDir } } : {}),
      ...(windowFrame.trim() ? { frame: windowFrame.trim() } : {}),
    };
    onStateChange(applyWindowFunction(transformState, clause));
    setWindowAnchor(null);
  }, [
    windowOutputCol,
    windowFunc,
    windowValueCol,
    windowNtileBuckets,
    windowOrderCol,
    windowOrderDir,
    windowPartitionBy,
    windowFrame,
    needsValueForWindow,
    needsOrderForWindow,
    windowTypeValidationError,
    ntileValidationError,
    onStateChange,
    transformState,
  ]);

  const handleRemoveWindow = useCallback((outputCol: string) => {
    onStateChange(removeWindowFunction(transformState, outputCol));
  }, [onStateChange, transformState]);

  // ── Global Actions ───────────────────────────────────────────────

  const handleClearAll = useCallback(() => {
    onStateChange(initStateFromTable(transformState.baseTable, transformState.limit));
  }, [onStateChange, transformState.baseTable, transformState.limit]);

  const handleToggleDistinct = useCallback(() => {
    onStateChange(applyDistinct(transformState, !transformState.distinct));
  }, [transformState, onStateChange]);

  // ── Quick-apply function chip ────────────────────────────────────

  const handleFunctionChip = useCallback((func: string) => {
    setAddColExpr(`${func}(${quoteIdent(columns[0] || 'column')})`);
  }, [columns]);

  // ── Helper: open a popover from a dropdown menu item ─────────────
  // Captures the group button element before closing the menu so the
  // existing event-based handlers can use it for Popover positioning.
  const openFromMenu = useCallback(
    (
      clearAnchor: () => void,
      anchor: HTMLElement | null,
      handler: (e: React.MouseEvent<HTMLElement>) => void,
    ) => {
      clearAnchor();
      if (anchor) handler({ currentTarget: anchor } as React.MouseEvent<HTMLElement>);
    },
    [],
  );

  // ── Render ───────────────────────────────────────────────────────

  return (
    <Box
      ref={toolbarRef}
      sx={{
        px: 1,
        py: 0.5,
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'wrap',
        bgcolor: '#fafbfc',
        minHeight: 36,
      }}
    >
      {/* ── Group 1: Filter & Sort ── */}
      <Button
        size="small"
        endIcon={<ChevronIcon sx={{ fontSize: 14 }} />}
        onClick={(e) => setFilterSortMenuAnchor(e.currentTarget)}
        disabled={disabled || columns.length === 0}
        sx={{
          fontSize: 11,
          textTransform: 'none',
          py: 0.25,
          px: 1,
          gap: 0.25,
          color:
            transformState.filters.length > 0 ||
              transformState.orderBy.length > 0 ||
              transformState.groupByColumns.length > 0 ||
              transformState.distinct
              ? 'primary.main'
              : 'text.secondary',
          fontWeight:
            transformState.filters.length > 0 ||
              transformState.orderBy.length > 0 ||
              transformState.groupByColumns.length > 0 ||
              transformState.distinct
              ? 600
              : 400,
        }}
      >
        <FilterIcon sx={{ fontSize: 13 }} />
        Filter &amp; Sort
      </Button>
      <Menu
        anchorEl={filterSortMenuAnchor}
        open={Boolean(filterSortMenuAnchor)}
        onClose={() => setFilterSortMenuAnchor(null)}
        MenuListProps={{ dense: true }}
        PaperProps={{ sx: { minWidth: 210, boxShadow: 3, borderRadius: 1.5 } }}
      >
        <MenuItem
          onClick={() => openFromMenu(() => setFilterSortMenuAnchor(null), filterSortMenuAnchor, handleOpenFilter)}
          disabled={columns.length === 0}
        >
          <ListItemIcon><FilterIcon sx={{ fontSize: 16, color: transformState.filters.length > 0 ? 'primary.main' : 'inherit' }} /></ListItemIcon>
          <ListItemText primary="Filter" secondary="Add WHERE conditions" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
          {transformState.filters.length > 0 && (
            <Chip label={transformState.filters.length} size="small" color="primary" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />
          )}
        </MenuItem>
        <MenuItem
          onClick={() => openFromMenu(() => setFilterSortMenuAnchor(null), filterSortMenuAnchor, handleOpenSort)}
          disabled={columns.length === 0}
        >
          <ListItemIcon><SortIcon sx={{ fontSize: 16, color: transformState.orderBy.length > 0 ? 'primary.main' : 'inherit' }} /></ListItemIcon>
          <ListItemText primary="Sort" secondary="ORDER BY columns" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
          {transformState.orderBy.length > 0 && (
            <Chip label={transformState.orderBy.length} size="small" color="primary" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />
          )}
        </MenuItem>
        <MenuItem
          onClick={() => openFromMenu(() => setFilterSortMenuAnchor(null), filterSortMenuAnchor, handleOpenGroupBy)}
          disabled={columns.length === 0}
        >
          <ListItemIcon><GroupIcon sx={{ fontSize: 16, color: transformState.groupByColumns.length > 0 ? 'secondary.main' : 'inherit' }} /></ListItemIcon>
          <ListItemText primary="Group By" secondary="Aggregate with GROUP BY" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
          {transformState.groupByColumns.length > 0 && (
            <Chip label="on" size="small" color="secondary" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />
          )}
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => { setFilterSortMenuAnchor(null); handleToggleDistinct(); }} >
          <ListItemIcon>
            <DistinctGlyphIcon sx={{ fontSize: 16, color: transformState.distinct ? 'primary.main' : 'inherit' }} />
          </ListItemIcon>
          <ListItemText primary="Distinct" secondary="Remove duplicate rows" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
          {transformState.distinct && (
            <Chip label="on" size="small" color="primary" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />
          )}
        </MenuItem>
      </Menu>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

      {/* ── Group 2: Combine ── */}
      <Button
        size="small"
        endIcon={<ChevronIcon sx={{ fontSize: 14 }} />}
        onClick={(e) => setCombineMenuAnchor(e.currentTarget)}
        disabled={disabled || (otherTables.length === 0 && columns.length < 2)}
        sx={{
          fontSize: 11,
          textTransform: 'none',
          py: 0.25,
          px: 1,
          gap: 0.25,
          color:
            transformState.joins.length > 0
              || (transformState.lookups || []).length > 0
              || transformState.unions.length > 0
              ? 'info.main'
              : 'text.secondary',
          fontWeight:
            transformState.joins.length > 0
              || (transformState.lookups || []).length > 0
              || transformState.unions.length > 0 ? 600 : 400,
        }}
      >
        <JoinIcon sx={{ fontSize: 13 }} />
        Combine
      </Button>
      <Menu
        anchorEl={combineMenuAnchor}
        open={Boolean(combineMenuAnchor)}
        onClose={() => setCombineMenuAnchor(null)}
        MenuListProps={{ dense: true }}
        PaperProps={{ sx: { minWidth: 210, boxShadow: 3, borderRadius: 1.5 } }}
      >
        <MenuItem
          onClick={() => openFromMenu(() => setCombineMenuAnchor(null), combineMenuAnchor, handleOpenJoin)}
          disabled={otherTables.length === 0}
        >
          <ListItemIcon><JoinIcon sx={{ fontSize: 16, color: transformState.joins.length > 0 ? 'info.main' : 'inherit' }} /></ListItemIcon>
          <ListItemText primary="Join" secondary="Join with another table" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
          {transformState.joins.length > 0 && (
            <Chip label={transformState.joins.length} size="small" color="info" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />
          )}
        </MenuItem>
        <MenuItem
          onClick={() => openFromMenu(() => setCombineMenuAnchor(null), combineMenuAnchor, handleOpenLookup)}
          disabled={otherTables.length === 0}
        >
          <ListItemIcon><LookupIcon sx={{ fontSize: 16, color: (transformState.lookups || []).length > 0 ? 'warning.main' : 'inherit' }} /></ListItemIcon>
          <ListItemText primary="Lookup" secondary="Enrich from reference table" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
          {(transformState.lookups || []).length > 0 && (
            <Chip label={(transformState.lookups || []).length} size="small" color="warning" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />
          )}
        </MenuItem>
        <MenuItem
          onClick={() => openFromMenu(() => setCombineMenuAnchor(null), combineMenuAnchor, handleOpenUnion)}
          disabled={otherTables.length === 0}
        >
          <ListItemIcon><UnionIcon sx={{ fontSize: 16, color: transformState.unions.length > 0 ? 'success.main' : 'inherit' }} /></ListItemIcon>
          <ListItemText primary="Union" secondary="Stack tables by column name" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
          {transformState.unions.length > 0 && (
            <Chip label={transformState.unions.length} size="small" color="success" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />
          )}
        </MenuItem>
        <MenuItem
          onClick={() => openFromMenu(() => setCombineMenuAnchor(null), combineMenuAnchor, handleOpenMerge)}
          disabled={columns.length < 2}
        >
          <ListItemIcon><MergeIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Merge Columns" secondary="Concatenate columns into one" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
        </MenuItem>
      </Menu>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

      {/* ── Group 3: Columns ── */}
      <Button
        size="small"
        endIcon={<ChevronIcon sx={{ fontSize: 14 }} />}
        onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
        disabled={disabled || columns.length === 0}
        sx={{ fontSize: 11, textTransform: 'none', py: 0.25, px: 1, gap: 0.25, color: 'text.secondary' }}
      >
        <AddColIcon sx={{ fontSize: 13 }} />
        Columns
      </Button>
      <Menu
        anchorEl={columnsMenuAnchor}
        open={Boolean(columnsMenuAnchor)}
        onClose={() => setColumnsMenuAnchor(null)}
        MenuListProps={{ dense: true }}
        PaperProps={{ sx: { minWidth: 210, boxShadow: 3, borderRadius: 1.5 } }}
      >
        <MenuItem
          onClick={() => openFromMenu(() => setColumnsMenuAnchor(null), columnsMenuAnchor, handleOpenAddCol)}
          disabled={columns.length === 0}
        >
          <ListItemIcon><AddColIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="+ Add Column" secondary="Computed expression" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
        </MenuItem>
        <MenuItem
          onClick={() => openFromMenu(() => setColumnsMenuAnchor(null), columnsMenuAnchor, handleOpenRename)}
          disabled={columns.length === 0}
        >
          <ListItemIcon><RenameColIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Rename" secondary="Rename a column" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
          {renamedColumns.length > 0 && (
            <Chip label={renamedColumns.length} size="small" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />
          )}
        </MenuItem>
        <MenuItem
          onClick={() => openFromMenu(() => setColumnsMenuAnchor(null), columnsMenuAnchor, handleOpenCast)}
          disabled={columns.length === 0}
        >
          <ListItemIcon><CastColIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Cast" secondary="Change data type" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
          {castedColumns.length > 0 && (
            <Chip label={castedColumns.length} size="small" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />
          )}
        </MenuItem>
        <MenuItem
          onClick={() => openFromMenu(() => setColumnsMenuAnchor(null), columnsMenuAnchor, handleOpenSplit)}
          disabled={columns.length === 0}
        >
          <ListItemIcon><SplitIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Split" secondary="Split by delimiter" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() => openFromMenu(() => setColumnsMenuAnchor(null), columnsMenuAnchor, handleOpenDrop)}
          disabled={columns.length === 0}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DropColIcon sx={{ fontSize: 16, color: transformState.excludeColumns.length > 0 ? 'error.main' : 'inherit' }} /></ListItemIcon>
          <ListItemText primary="Drop Column(s)" secondary="Remove from output" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
          {transformState.excludeColumns.length > 0 && (
            <Chip label={transformState.excludeColumns.length} size="small" color="error" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />
          )}
        </MenuItem>
      </Menu>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

      {/* ── Group 4: Transform ── */}
      <Button
        size="small"
        endIcon={<ChevronIcon sx={{ fontSize: 14 }} />}
        onClick={(e) => setTransformMenuAnchor(e.currentTarget)}
        disabled={disabled || columns.length === 0}
        sx={{ fontSize: 11, textTransform: 'none', py: 0.25, px: 1, gap: 0.25, color: 'text.secondary' }}
      >
        <ReplaceIcon sx={{ fontSize: 13 }} />
        Transform
      </Button>
      <Menu
        anchorEl={transformMenuAnchor}
        open={Boolean(transformMenuAnchor)}
        onClose={() => setTransformMenuAnchor(null)}
        MenuListProps={{ dense: true }}
        PaperProps={{ sx: { minWidth: 220, boxShadow: 3, borderRadius: 1.5 } }}
      >
        <MenuItem
          onClick={() => openFromMenu(() => setTransformMenuAnchor(null), transformMenuAnchor, handleOpenReplaceMissing)}
          disabled={columns.length === 0}
        >
          <ListItemIcon><FillMissingIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Fill Nulls" secondary="Replace missing values" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
          {replaceMissingColumnsActive.length > 0 && (
            <Chip label={replaceMissingColumnsActive.length} size="small" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />
          )}
        </MenuItem>
        <MenuItem
          onClick={() => openFromMenu(() => setTransformMenuAnchor(null), transformMenuAnchor, handleOpenReplace)}
          disabled={columns.length === 0}
        >
          <ListItemIcon><ReplaceIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Replace" secondary="Find &amp; replace values" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
          {replacePatternColumnsActive.length > 0 && (
            <Chip label={replacePatternColumnsActive.length} size="small" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />
          )}
        </MenuItem>
        <MenuItem
          onClick={() => openFromMenu(() => setTransformMenuAnchor(null), transformMenuAnchor, handleOpenDateFormat)}
          disabled={dateFormatSelectableColumns.length === 0}
        >
          <ListItemIcon><DateFormatIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Date Format" secondary="Reformat date columns" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
        </MenuItem>
        <MenuItem
          onClick={() => openFromMenu(() => setTransformMenuAnchor(null), transformMenuAnchor, handleOpenCase)}
          disabled={columns.length === 0}
        >
          <ListItemIcon><CaseIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Case / If" secondary="Conditional expression" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
          {caseExpressions.length > 0 && (
            <Chip label={caseExpressions.length} size="small" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />
          )}
        </MenuItem>
        <MenuItem
          onClick={() => openFromMenu(() => setTransformMenuAnchor(null), transformMenuAnchor, handleOpenWindow)}
          disabled={columns.length === 0}
        >
          <ListItemIcon><WindowIcon sx={{ fontSize: 16, color: windowFunctions.length > 0 ? 'warning.main' : 'inherit' }} /></ListItemIcon>
          <ListItemText primary="Window" secondary="Analytic window function" primaryTypographyProps={{ fontSize: 12 }} secondaryTypographyProps={{ fontSize: 10 }} />
          {windowFunctions.length > 0 && (
            <Chip label={windowFunctions.length} size="small" color="warning" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />
          )}
        </MenuItem>
      </Menu>

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Active transform badges */}
      {transformState.filters.length > 0 && (
        <Chip
          label={`${transformState.filters.length} filter${transformState.filters.length > 1 ? 's' : ''}`}
          size="small"
          color={toolbarChipColor('filter', 'primary')}
          variant="outlined"
          onClick={handleOpenFilter as any}
          onDelete={() => handleRemoveTransformChip('filter')}
          sx={{ fontSize: 10, height: 20 }}
        />
      )}
      {transformState.groupByColumns.length > 0 && (
        <Chip
          label={`${transformState.groupByColumns.length} group by${transformState.groupByColumns.length > 1 ? 's' : ''}`}
          size="small"
          color={toolbarChipColor('groupBy', 'secondary')}
          variant="outlined"
          onClick={handleOpenGroupBy as any}
          onDelete={() => handleRemoveTransformChip('groupBy')}
          sx={{ fontSize: 10, height: 20 }}
        />
      )}
      {transformState.orderBy.length > 0 && (
        <Chip
          label={`${transformState.orderBy.length} sort${transformState.orderBy.length > 1 ? 's' : ''}`}
          size="small"
          color={toolbarChipColor('sort')}
          variant="outlined"
          onClick={handleOpenSort as any}
          onDelete={() => handleRemoveTransformChip('sort')}
          sx={{ fontSize: 10, height: 20 }}
        />
      )}
      {addedColumnsForToolbarBadge.length > 0 && (
        <Chip
          label={`${addedColumnsForToolbarBadge.length} column${addedColumnsForToolbarBadge.length > 1 ? 's' : ''}`}
          size="small"
          color={toolbarChipColor('column')}
          variant="outlined"
          onClick={handleOpenAddCol as any}
          onDelete={() => handleRemoveTransformChip('column')}
          sx={{ fontSize: 10, height: 20 }}
        />
      )}
      {transformState.joins.length > 0 && (
        <Chip
          label={`${transformState.joins.length} join${transformState.joins.length > 1 ? 's' : ''}`}
          size="small"
          color={toolbarChipColor('join', 'info')}
          variant="outlined"
          onDelete={() => handleRemoveTransformChip('join')}
          sx={{ fontSize: 10, height: 20 }}
        />
      )}
      {transformState.unions.length > 0 && (
        <Chip
          label={`${transformState.unions.length} union${transformState.unions.length > 1 ? 's' : ''}`}
          size="small"
          color={toolbarChipColor('union', 'success')}
          variant="outlined"
          onClick={handleOpenUnion as any}
          onDelete={() => handleRemoveTransformChip('union')}
          sx={{ fontSize: 10, height: 20 }}
        />
      )}
      {replaceMissingColumnsActive.length > 0 && (
        <Chip
          label={`${replaceMissingColumnsActive.length} filled`}
          size="small"
          color={toolbarChipColor('filled', 'default')}
          variant="outlined"
          onClick={handleOpenReplaceMissing as any}
          onDelete={() => handleRemoveTransformChip('filled')}
          sx={{ fontSize: 10, height: 20 }}
        />
      )}
      {replacePatternColumnsActive.length > 0 && (
        <Chip
          label={`${replacePatternColumnsActive.length} replaced`}
          size="small"
          color={toolbarChipColor('replaced', 'default')}
          variant="outlined"
          onClick={handleOpenReplace as any}
          onDelete={() => handleRemoveTransformChip('replaced')}
          sx={{ fontSize: 10, height: 20 }}
        />
      )}
      {(transformState.lookups || []).length > 0 && (
        <Chip
          label={`${(transformState.lookups || []).length} lookup${(transformState.lookups || []).length > 1 ? 's' : ''}`}
          size="small"
          color={toolbarChipColor('lookup', 'warning')}
          variant="outlined"
          onClick={handleOpenLookup as any}
          onDelete={() => handleRemoveTransformChip('lookup')}
          sx={{ fontSize: 10, height: 20 }}
        />
      )}
      {caseExpressions.length > 0 && (
        <Chip
          label={`${caseExpressions.length} case`}
          size="small"
          color={toolbarChipColor('case', 'default')}
          variant="outlined"
          onClick={handleOpenCase as any}
          onDelete={() => handleRemoveTransformChip('case')}
          sx={{ fontSize: 10, height: 20 }}
        />
      )}
      {renamedColumns.length > 0 && (
        <Chip
          label={`${renamedColumns.length} renamed`}
          size="small"
          color={toolbarChipColor('renamed', 'default')}
          variant="outlined"
          onClick={handleOpenRename as any}
          onDelete={() => handleRemoveTransformChip('renamed')}
          sx={{ fontSize: 10, height: 20 }}
        />
      )}
      {castedColumns.length > 0 && (
        <Chip
          label={`${castedColumns.length} cast`}
          size="small"
          color={toolbarChipColor('cast', 'default')}
          variant="outlined"
          onClick={handleOpenCast as any}
          onDelete={() => handleRemoveTransformChip('cast')}
          sx={{ fontSize: 10, height: 20 }}
        />
      )}
      {transformState.excludeColumns.length > 0 && (
        <Chip
          label={`${transformState.excludeColumns.length} dropped`}
          size="small"
          color={toolbarChipColor('dropped', 'default')}
          variant="outlined"
          onClick={handleOpenDrop as any}
          onDelete={() => handleRemoveTransformChip('dropped')}
          sx={{ fontSize: 10, height: 20 }}
        />
      )}
      {windowFunctions.length > 0 && (
        <Chip
          label={`${windowFunctions.length} window${windowFunctions.length > 1 ? 's' : ''}`}
          size="small"
          color={toolbarChipColor('window', 'warning')}
          variant="outlined"
          onClick={handleOpenWindow as any}
          onDelete={() => handleRemoveTransformChip('window')}
          sx={{ fontSize: 10, height: 20 }}
        />
      )}

      {/* Undo / Redo — always visible; disabled when unavailable */}
      {onUndo && (
        <Tooltip title={canUndo ? 'Undo last toolbar action' : 'Nothing to undo'}>
          <span>
            <IconButton size="small" onClick={onUndo} disabled={!canUndo} sx={{ p: 0.25 }}>
              <UndoIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {onRedo && (
        <Tooltip title={canRedo ? 'Redo' : 'Nothing to redo'}>
          <span>
            <IconButton size="small" onClick={onRedo} disabled={!canRedo} sx={{ p: 0.25 }}>
              <RedoIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Clear All */}
      {modified && (
        <Tooltip title="Clear all transforms">
          <IconButton size="small" onClick={handleClearAll} sx={{ p: 0.25 }}>
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* ── Filter Popover ──────────────────────────────────────── */}
      <Popover
        open={!!filterAnchor}
        anchorEl={filterAnchor}
        onClose={() => { setFilterAnchor(null); setEditingFilterId(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 340 } }}
      >
        <TransformPopoverHeader
          title={editingFilterId ? 'Edit Filter' : 'Add Filter'}
          onClose={() => { setFilterAnchor(null); setEditingFilterId(null); }}
        />

        {/* Active filters — click to edit, AND/OR badge between them */}
        {transformState.filters.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {transformState.filters.map((f, i) => (
              <Stack key={f.id} spacing={0.5}>
                {i > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={f.logicOp || 'AND'}
                      onChange={(_, val) => {
                        if (!val) return;
                        onStateChange(updateFilter(transformState, f.id, { logicOp: val }));
                      }}
                      sx={{ height: 18 }}
                    >
                      <ToggleButton value="AND" sx={{ fontSize: 9, px: 0.75, py: 0 }}>AND</ToggleButton>
                      <ToggleButton value="OR" sx={{ fontSize: 9, px: 0.75, py: 0 }}>OR</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}
                <Chip
                  label={`${f.negate ? 'NOT ' : ''}${f.column} ${f.operator}${f.value ? ` ${f.value}` : ''}`}
                  size="small"
                  onClick={(e) => handleEditFilter(f, e)}
                  onDelete={() => handleRemoveFilter(f.id)}
                  color={editingFilterId === f.id ? 'primary' : 'default'}
                  sx={{ fontSize: 11, cursor: 'pointer' }}
                />
              </Stack>
            ))}
            <Divider sx={{ my: 0.5 }} />
          </Stack>
        )}

        <Stack spacing={1.5}>
          {/* AND/OR toggle for the new filter being added (only shown when filters exist) */}
          {!editingFilterId && transformState.filters.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary">Connect with:</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={pendingFilterLogicOp}
                onChange={(_, val) => { if (val) setPendingFilterLogicOp(val); }}
                sx={{ height: 22 }}
              >
                <ToggleButton value="AND" sx={{ fontSize: 10, px: 1, py: 0 }}>AND</ToggleButton>
                <ToggleButton value="OR" sx={{ fontSize: 10, px: 1, py: 0 }}>OR</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}

          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Column</InputLabel>
            <Select
              value={filterColumn}
              onChange={(e) => setFilterColumn(e.target.value)}
              label="Column"
              sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}
            >
              {columns.map((col) => (
                <MenuItem key={col} value={col} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                  {col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Operator</InputLabel>
            <Select
              value={filterOperator}
              onChange={(e) => setFilterOperator(e.target.value as FilterOperator)}
              label="Operator"
              sx={{ fontSize: 12 }}
            >
              {FILTER_OPERATORS.map((op) => (
                <MenuItem key={op.value} value={op.value} sx={{ fontSize: 12 }}>
                  {op.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {needsValue && (
            <TextField
              size="small"
              label="Value"
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              placeholder={filterOperator === 'IN' ? 'val1, val2, val3' : 'Enter value'}
              fullWidth
              sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }}
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={filterNegate}
                onChange={(e) => setFilterNegate(e.target.checked)}
              />
            }
            label="Exclude matching rows"
            componentsProps={{ typography: { fontSize: 12 } }}
            sx={{ ml: -0.5 }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={handleApplyFilter}
            disabled={!filterColumn || (needsValue && !filterValue.trim())}
            sx={{ fontSize: 12 }}
          >
            {editingFilterId ? 'Update Filter' : 'Add Filter'}
          </Button>
        </Stack>
      </Popover>

      {/* ── Sort Popover ────────────────────────────────────────── */}
      <Popover
        open={!!sortAnchor}
        anchorEl={sortAnchor}
        onClose={() => setSortAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 280 } }}
      >
        <TransformPopoverHeader title="Sort By" onClose={() => setSortAnchor(null)} />

        {/* Active sorts */}
        {transformState.orderBy.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {transformState.orderBy.map((o) => (
              <Chip
                key={o.column}
                label={`${o.column} ${o.direction}`}
                size="small"
                onDelete={() => handleRemoveSort(o.column)}
                sx={{ fontSize: 11 }}
              />
            ))}
          </Stack>
        )}

        <Stack spacing={1.5}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Column</InputLabel>
            <Select
              value={sortColumn}
              onChange={(e) => setSortColumn(e.target.value)}
              label="Column"
              sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}
            >
              {columns.map((col) => (
                <MenuItem key={col} value={col} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                  {col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant={sortDirection === 'ASC' ? 'contained' : 'outlined'}
              onClick={() => setSortDirection('ASC')}
              sx={{ flex: 1, fontSize: 11 }}
            >
              Ascending
            </Button>
            <Button
              size="small"
              variant={sortDirection === 'DESC' ? 'contained' : 'outlined'}
              onClick={() => setSortDirection('DESC')}
              sx={{ flex: 1, fontSize: 11 }}
            >
              Descending
            </Button>
          </Stack>

          <Button
            variant="contained"
            size="small"
            onClick={handleApplySort}
            disabled={!sortColumn}
            sx={{ fontSize: 12 }}
          >
            Apply Sort
          </Button>
        </Stack>
      </Popover>

      {/* ── Group By Popover ────────────────────────────────────── */}
      <Popover
        open={!!groupByAnchor}
        anchorEl={groupByAnchor}
        onClose={() => setGroupByAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 360 } }}
      >
        <TransformPopoverHeader title="Group By" onClose={() => setGroupByAnchor(null)} />

        {/* Group columns multi-select */}
        <Autocomplete
          multiple
          size="small"
          options={columns}
          value={gbColumns}
          onChange={(_, val) => setGbColumns(val)}
          renderInput={(params) => (
            <TextField {...params} label="Group By Columns" placeholder="Select columns" sx={{ mb: 1.5 }} />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={option}
                size="small"
                sx={{ fontSize: 10, fontFamily: '"Fira Code", monospace' }}
              />
            ))
          }
          sx={{
            '& .MuiInputBase-root': {
              fontSize: 12,
              fontFamily: '"Fira Code", monospace'
            }
          }}
        />

        {/* Aggregations */}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Aggregations
        </Typography>
        {gbAggs.map((agg, i) => (
          <Chip
            key={i}
            label={`${agg.func}(${agg.column})`}
            size="small"
            onDelete={() => setGbAggs((prev) => prev.filter((_, idx) => idx !== i))}
            sx={{ fontSize: 11, mr: 0.5, mb: 0.5 }}
          />
        ))}
        <Stack direction="row" spacing={1} sx={{ mt: 0.5, mb: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={gbAggFunc}
              onChange={(e) => setGbAggFunc(e.target.value as AggregateFunc)}
              sx={{ fontSize: 12 }}
            >
              {(['COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'COUNT_DISTINCT'] as AggregateFunc[]).map((f) => (
                <MenuItem key={f} value={f} sx={{ fontSize: 12 }}>{f}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={gbAggCol}
              onChange={(e) => setGbAggCol(e.target.value)}
              sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}
              displayEmpty
              renderValue={(v) => v || 'Column...'}
            >
              {columns.map((col) => (
                <MenuItem key={col} value={col} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                  {col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={handleAddAgg} disabled={!gbAggCol} sx={{ fontSize: 11 }}>
            Add
          </Button>
        </Stack>

        {/* HAVING section */}
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          HAVING (filter on aggregated results)
        </Typography>
        {gbHaving.map((h, i) => (
          <Chip
            key={h.id}
            label={`${h.aggregateFunc === 'COUNT_STAR' ? 'COUNT(*)' : `${h.aggregateFunc}(${h.column})`} ${h.operator} ${h.value}`}
            size="small"
            onDelete={() => setGbHaving((prev) => prev.filter((_, idx) => idx !== i))}
            sx={{ fontSize: 11, mr: 0.5, mb: 0.5 }}
          />
        ))}
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, mb: 1 }} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select
              value={gbHavingAggFunc}
              onChange={(e) => setGbHavingAggFunc(e.target.value as AggregateFunc | 'COUNT_STAR')}
              sx={{ fontSize: 11 }}
            >
              {(['COUNT_STAR', 'COUNT', 'SUM', 'AVG', 'MIN', 'MAX'] as const).map((f) => (
                <MenuItem key={f} value={f} sx={{ fontSize: 11 }}>
                  {f === 'COUNT_STAR' ? 'COUNT(*)' : f}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {gbHavingAggFunc !== 'COUNT_STAR' && (
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <Select
                value={gbHavingAggCol}
                onChange={(e) => setGbHavingAggCol(e.target.value)}
                sx={{ fontSize: 11, fontFamily: '"Fira Code", monospace' }}
                displayEmpty
                renderValue={(v) => v || 'Column...'}
              >
                {columns.map((col) => (
                  <MenuItem key={col} value={col} sx={{ fontSize: 11, fontFamily: '"Fira Code", monospace' }}>{col}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 50 }}>
            <Select
              value={gbHavingOp}
              onChange={(e) => setGbHavingOp(e.target.value as HavingOperator)}
              sx={{ fontSize: 11 }}
            >
              {(['>', '<', '>=', '<=', '=', '!='] as HavingOperator[]).map((op) => (
                <MenuItem key={op} value={op} sx={{ fontSize: 11 }}>{op}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            value={gbHavingVal}
            onChange={(e) => setGbHavingVal(e.target.value)}
            placeholder="Value"
            sx={{
              width: 70,
              '& .MuiOutlinedInput-root': { fontSize: 11 },
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddHaving(); }}
          />
          <Button size="small" variant="outlined" onClick={handleAddHaving} disabled={!gbHavingVal.trim()} sx={{ fontSize: 11 }}>
            Add
          </Button>
        </Stack>

        <Button
          variant="contained"
          size="small"
          onClick={handleApplyGroupBy}
          fullWidth
          sx={{ fontSize: 12 }}
        >
          {gbColumns.length === 0 && gbAggs.length === 0 && gbHaving.length === 0 ? 'Clear Group By' : 'Apply Group By'}
        </Button>
      </Popover>

      {/* ── Join Popover ────────────────────────────────────────── */}
      <Popover
        open={!!joinAnchor}
        anchorEl={joinAnchor}
        onClose={() => setJoinAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 340 } }}
      >
        <TransformPopoverHeader title="Join Table" onClose={() => setJoinAnchor(null)} />

        {/* Active joins */}
        {transformState.joins.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {transformState.joins.map((j) => (
              <Chip
                key={j.id}
                label={`${j.joinType} JOIN ${j.table} ON ${j.conditions.map((c) => `${c.leftColumn}=${c.rightColumn}`).join(' AND ')}`}
                size="small"
                onDelete={() => onStateChange(removeJoin(transformState, j.id))}
                sx={{ fontSize: 10 }}
              />
            ))}
          </Stack>
        )}

        <Stack spacing={1.5}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Join Type</InputLabel>
            <Select
              value={joinType}
              onChange={(e) => setJoinType(e.target.value as JoinType)}
              label="Join Type"
              sx={{ fontSize: 12 }}
            >
              {(['INNER', 'LEFT', 'RIGHT', 'FULL OUTER'] as JoinType[]).map((t) => (
                <MenuItem key={t} value={t} sx={{ fontSize: 12 }}>{t} JOIN</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Table</InputLabel>
            <Select
              value={joinTable}
              onChange={(e) => {
                setJoinTable(e.target.value);
                setJoinConditions([{ leftColumn: columns[0] || '', rightColumn: '' }]);
                setJoinColumns([]);
              }}
              label="Table"
              sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}
            >
              {otherTables.map((t) => (
                <MenuItem key={t.name} value={t.name} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Join conditions — one row per condition, plus Add condition button */}
          <Typography variant="caption" color="text.secondary">Join Conditions (ON)</Typography>
          {joinConditions.map((cond, idx) => (
            <Stack key={idx} spacing={0.5}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={cond.leftColumn}
                    onChange={(e) => setJoinConditions((prev) => prev.map((c, i) => i === idx ? { ...c, leftColumn: e.target.value } : c))}
                    displayEmpty
                    renderValue={(v) => v || 'Left col...'}
                    sx={{ fontSize: 11, fontFamily: '"Fira Code", monospace' }}
                  >
                    {columns.map((col) => (
                      <MenuItem key={col} value={col} sx={{ fontSize: 11, fontFamily: '"Fira Code", monospace' }}>{col}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" sx={{ px: 0.5 }}>=</Typography>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={cond.rightColumn}
                    onChange={(e) => setJoinConditions((prev) => prev.map((c, i) => i === idx ? { ...c, rightColumn: e.target.value } : c))}
                    displayEmpty
                    renderValue={(v) => v || 'Right col...'}
                    sx={{ fontSize: 11, fontFamily: '"Fira Code", monospace' }}
                  >
                    {joinTableColumns.map((col) => (
                      <MenuItem key={col.name} value={col.name} sx={{ fontSize: 11, fontFamily: '"Fira Code", monospace' }}>{col.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {joinConditions.length > 1 && (
                  <IconButton size="small" onClick={() => setJoinConditions((prev) => prev.filter((_, i) => i !== idx))} sx={{ p: 0.25 }}>
                    <ClearIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                )}
              </Stack>
              {joinConditionErrors[idx] && (
                <Typography variant="caption" sx={{ color: 'error.main', fontSize: 10 }}>
                  ⚠ {joinConditionErrors[idx]}
                </Typography>
              )}
            </Stack>
          ))}
          <Button
            size="small"
            variant="text"
            onClick={() => setJoinConditions((prev) => [...prev, { leftColumn: columns[0] || '', rightColumn: '' }])}
            sx={{ fontSize: 11, alignSelf: 'flex-start' }}
          >
            + Add condition
          </Button>

          <FormControl size="small" fullWidth disabled={!joinTable}>
            <InputLabel sx={{ fontSize: 12 }}>Columns to include from joined table</InputLabel>
            <Select
              multiple
              value={joinColumns}
              onChange={(e) => setJoinColumns(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
              label="Columns to include from joined table"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" sx={{ fontSize: 10, height: 18, bgcolor: 'action.selected' }} />
                  ))}
                </Box>
              )}
              sx={{ '& .MuiSelect-select': { fontSize: 12, display: 'flex', alignItems: 'center', py: 0.5, minHeight: 28 } }}
            >
              {joinTableColumns.map((col) => (
                <MenuItem key={col.name} value={col.name} sx={{ fontSize: 11, fontFamily: '"Fira Code", monospace' }}>
                  {col.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            size="small"
            onClick={handleApplyJoin}
            disabled={!joinTable || joinConditions.every((c) => !c.leftColumn || !c.rightColumn) || hasJoinErrors}
            sx={{ fontSize: 12 }}
          >
            Apply Join
          </Button>
        </Stack>
      </Popover>

      {/* ── Union Popover ───────────────────────────────────────── */}
      <Popover
        open={!!unionAnchor}
        anchorEl={unionAnchor}
        onClose={() => setUnionAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 360, maxWidth: 420 } }}
      >
        <TransformPopoverHeader title="Union Tables" onClose={() => setUnionAnchor(null)} />

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.4 }}>
          Stack rows from another table beneath the current result.
        </Typography>

        {transformState.unions.length > 0 && (
          <Stack spacing={0.75} sx={{ mb: 1.5 }}>
            {transformState.unions.map((u) => (
              <Chip
                key={u.id}
                label={`${transformState.unionDeduplicate ? 'UNION' : 'UNION ALL'} ${u.table}${(u.columns || []).length > 0 ? ` (${(u.columns || []).length} cols)` : ''}`}
                size="small"
                onDelete={() => onStateChange(removeUnion(transformState, u.id))}
                sx={{ fontSize: 10, alignSelf: 'flex-start' }}
              />
            ))}
          </Stack>
        )}

        <Stack spacing={1.5}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Row mode</InputLabel>
            <Select
              value={unionDeduplicate ? 'dedupe' : 'all'}
              onChange={(e) => setUnionDeduplicateLocal(e.target.value === 'dedupe')}
              label="Row mode"
              sx={{ fontSize: 12 }}
            >
              <MenuItem value="all" sx={{ fontSize: 12 }}>
                UNION ALL — keep all rows (recommended, fastest)
              </MenuItem>
              <MenuItem value="dedupe" sx={{ fontSize: 12 }}>
                UNION — remove duplicate rows (slower)
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Column alignment</InputLabel>
            <Select
              value={unionAlignMode}
              onChange={(e) => setUnionAlignModeLocal(e.target.value as UnionAlignMode)}
              label="Column alignment"
              sx={{ fontSize: 12 }}
            >
              <MenuItem value="auto" sx={{ fontSize: 12 }}>
                Auto (recommended) — match columns by name
              </MenuItem>
              <MenuItem value="by_position" sx={{ fontSize: 12 }}>
                By position (advanced) — column order must match exactly
              </MenuItem>
            </Select>
          </FormControl>

          {unionTableOptions.length > 0 && (
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: 12 }}>Table</InputLabel>
              <Select
                value={unionTable}
                onChange={(e) => {
                  setUnionTable(e.target.value);
                  setUnionColumns([]);
                }}
                label="Table"
                sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}
              >
                {unionTableOptions.map((name) => (
                  <MenuItem key={name} value={name} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl size="small" fullWidth disabled={!unionTable || transformState.unions.some((u) => u.table === unionTable)}>
            <InputLabel sx={{ fontSize: 12 }}>Columns (optional — limits I/O)</InputLabel>
            <Select
              multiple
              value={unionColumns}
              onChange={(e) => setUnionColumns(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
              label="Columns (optional — limits I/O)"
              renderValue={(selected) => (
                (selected as string[]).length === 0
                  ? <Typography variant="caption" color="text.secondary">All columns (by name)</Typography>
                  : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" sx={{ fontSize: 10, height: 18, bgcolor: 'action.selected' }} />
                      ))}
                    </Box>
                  )
              )}
              sx={{ '& .MuiSelect-select': { fontSize: 12, display: 'flex', alignItems: 'center', py: 0.5, minHeight: 28 } }}
            >
              {unionTableColumns.map((col) => (
                <MenuItem key={col.name} value={col.name} sx={{ fontSize: 11, fontFamily: '"Fira Code", monospace' }}>
                  {col.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            size="small"
            onClick={handleApplyUnion}
            disabled={!canApplyUnion}
            sx={{ fontSize: 12 }}
          >
            {unionApplyLabel}
          </Button>
        </Stack>
      </Popover>

      {/* ── Lookup Popover ────────────────────────────────────────────────── */}
      <Popover
        open={!!lookupAnchor}
        anchorEl={lookupAnchor}
        onClose={() => setLookupAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 400, maxWidth: 600 } }}
      >
        <TransformPopoverHeader title="Lookup Column (VLOOKUP)" onClose={() => setLookupAnchor(null)} />

        <Stack spacing={1.5}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Reference Table</InputLabel>
            <Select
              value={lookupTable}
              onChange={(e) => {
                setLookupTable(e.target.value);
                setLookupRefKey('');
                setLookupColumns([]);
                fetchLookupPreview(e.target.value);
              }}
              label="Reference Table"
              sx={{ '& .MuiSelect-select': { fontSize: 12 } }}
            >
              {otherTables.map((t) => (
                <MenuItem key={t.name} value={t.name} sx={{ fontSize: 12 }}>
                  {t.name} ({t.columns.length} cols)
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormControl size="small">
              <InputLabel sx={{ fontSize: 12 }}>Source Key (this table)</InputLabel>
              <Select
                value={lookupSourceKey}
                onChange={(e) => setLookupSourceKey(e.target.value)}
                label="Source Key (this table)"
                sx={{ '& .MuiSelect-select': { fontSize: 12, fontFamily: '"Fira Code", monospace', py: 0.75 } }}
              >
                {columns.map((col) => (
                  <MenuItem key={col} value={col} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>{col}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel sx={{ fontSize: 12 }}>Reference Key (ref table)</InputLabel>
              <Select
                value={lookupRefKey}
                onChange={(e) => setLookupRefKey(e.target.value)}
                label="Reference Key (ref table)"
                disabled={!lookupTable}
                sx={{ '& .MuiSelect-select': { fontSize: 12, fontFamily: '"Fira Code", monospace', py: 0.75 } }}
              >
                {(otherTables.find((t) => t.name === lookupTable)?.columns || []).map((col) => (
                  <MenuItem key={col.name} value={col.name} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>{col.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <FormControl size="small" fullWidth disabled={!lookupTable}>
            <InputLabel sx={{ fontSize: 12 }}>Columns to bring in</InputLabel>
            <Select
              multiple
              value={lookupColumns}
              onChange={(e) => setLookupColumns(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
              label="Columns to bring in"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" sx={{ fontSize: 10, height: 18, bgcolor: 'action.selected' }} />
                  ))}
                </Box>
              )}
              sx={{ '& .MuiSelect-select': { fontSize: 12, display: 'flex', alignItems: 'center', py: 0.5, minHeight: 28 } }}
            >
              {(otherTables.find((t) => t.name === lookupTable)?.columns || []).map((col) => (
                <MenuItem key={col.name} value={col.name} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                  {col.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Match Behavior</Typography>
              <ToggleButtonGroup
                size="small"
                value={lookupMatchBehavior}
                exclusive
                onChange={(_, val) => val && setLookupMatchBehavior(val)}
                fullWidth
              >
                <ToggleButton value="first" sx={{ fontSize: 10, py: 0.5 }}>First Match</ToggleButton>
                <ToggleButton value="all" sx={{ fontSize: 10, py: 0.5 }}>All Matches</ToggleButton>
              </ToggleButtonGroup>
              {lookupMatchBehavior === 'all' ? (
                <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block', fontSize: 10 }}>
                  Warning: May multiply rows if keys aren't unique.
                </Typography>
              ) : (
                <FormControlLabel
                  control={<Checkbox size="small" checked={lookupIsUnique} onChange={(e) => setLookupIsUnique(e.target.checked)} sx={{ p: 0.5 }} />}
                  label={<Typography variant="caption" sx={{ fontSize: 10 }}>Ref keys are unique</Typography>}
                  sx={{ mt: 0.5, ml: 0 }}
                />
              )}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Unmatched Rows</Typography>
              <ToggleButtonGroup
                size="small"
                value={lookupKeepUnmatched ? 'keep' : 'drop'}
                exclusive
                onChange={(_, val) => val && setLookupKeepUnmatched(val === 'keep')}
                fullWidth
              >
                <ToggleButton value="keep" sx={{ fontSize: 10, py: 0.5 }}>Keep (NULL)</ToggleButton>
                <ToggleButton value="drop" sx={{ fontSize: 10, py: 0.5 }}>Drop</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Reference Preview */}
          {lookupTable && (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: '#f8fafc' }}>
              <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                Reference Preview: {lookupTable}
              </Typography>
              {lookupPreviewLoading ? (
                <CircularProgress size={20} sx={{ m: 2 }} />
              ) : lookupPreview ? (
                <TableContainer sx={{ maxHeight: 120 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {lookupPreview.columns.map((c: string) => (
                          <TableCell key={c} sx={{ fontSize: 10, py: 0.5, bgcolor: '#f1f5f9' }}>{c}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lookupPreview.rows.map((row: any, i: number) => (
                        <TableRow key={i}>
                          {lookupPreview.columns.map((c: string) => (
                            <TableCell key={c} sx={{ fontSize: 10, py: 0.5 }}>{String(row[c] ?? '')}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="caption" color="text.secondary">No preview available</Typography>
              )}
            </Box>
          )}
          {/* Current Lookups List */}
          {(transformState.lookups || []).length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                Current Lookups:
              </Typography>
              <Stack spacing={0.5}>
                {(transformState.lookups || []).map((l, i) => (
                  <Box
                    key={l.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1,
                      py: 0.5,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: 10 }}>
                      <strong>L{i + 1}</strong>: {l.refTable} ({l.columns.join(', ')})
                    </Typography>
                    <IconButton size="small" onClick={() => handleRemoveLookup(l.id)} sx={{ p: 0 }}>
                      <ClearIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          <Button
            variant="contained"
            size="small"
            onClick={handleApplyLookup}
            disabled={!lookupTable || !lookupSourceKey || !lookupRefKey || lookupColumns.length === 0}
            sx={{ fontSize: 12 }}
          >
            Add Lookup
          </Button>
        </Stack>
      </Popover>

      {/* ── Add Column Popover ──────────────────────────────────── */}
      <Popover
        open={!!addColAnchor}
        anchorEl={addColAnchor}
        onClose={() => setAddColAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 320 } }}
      >
        <TransformPopoverHeader title="Add Computed Column" onClose={() => setAddColAnchor(null)} />

        {addedColumns.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {addedColumns.map((column) => (
              <Chip
                key={column.alias}
                label={column.alias}
                size="small"
                onDelete={() => handleRemoveAddedColumn(column.alias)}
                sx={{ fontSize: 11 }}
              />
            ))}
          </Stack>
        )}

        {/* Function chips */}
        <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
          {['UPPER', 'LOWER', 'TRIM', 'ROUND', 'COALESCE', 'LENGTH'].map((fn) => (
            <Chip
              key={fn}
              label={fn}
              size="small"
              onClick={() => handleFunctionChip(fn)}
              sx={{ fontSize: 10, cursor: 'pointer' }}
            />
          ))}
        </Stack>

        <Stack spacing={1.5}>
          <TextField
            size="small"
            label="Expression"
            value={addColExpr}
            onChange={(e) => setAddColExpr(e.target.value)}
            placeholder='UPPER("column_name")'
            fullWidth
            multiline
            maxRows={3}
            sx={{ '& textarea': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
          />

          <TextField
            size="small"
            label="Column Alias"
            value={addColAlias}
            onChange={(e) => setAddColAlias(e.target.value)}
            placeholder="new_column_name"
            fullWidth
            sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleApplyAddCol(); }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={handleApplyAddCol}
            disabled={!addColExpr.trim() || !addColAlias.trim()}
            sx={{ fontSize: 12 }}
          >
            Add Column
          </Button>
        </Stack>
      </Popover>

      {/* ── Replace Missing Popover ─────────────────────────────── */}
      <Popover
        open={!!replaceMissingAnchor}
        anchorEl={replaceMissingAnchor}
        onClose={() => setReplaceMissingAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 360 } }}
      >
        <TransformPopoverHeader title="Fill nulls" onClose={() => setReplaceMissingAnchor(null)} />

        {replaceMissingColumnsActive.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {replaceMissingColumnsActive.map((column) => (
              <Chip
                key={column.alias}
                label={column.alias}
                size="small"
                sx={{ fontSize: 11, alignSelf: 'flex-start' }}
              />
            ))}
          </Stack>
        )}

        <Stack spacing={1.5}>
          <Autocomplete
            multiple
            size="small"
            options={replaceMissingSelectableColumns}
            value={replaceMissingColumns}
            onChange={(_, value) => setReplaceMissingColumns(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Columns"
                placeholder={replaceMissingSelectableColumns.length > 0 ? 'Select columns' : 'No compatible columns'}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  sx={{ fontSize: 10, fontFamily: '"Fira Code", monospace' }}
                />
              ))
            }
            sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
          />

          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Strategy</InputLabel>
            <Select
              value={replaceMissingStrategy}
              onChange={(e) => {
                const nextStrategy = e.target.value as ReplaceMissingStrategy;
                setReplaceMissingStrategy(nextStrategy);
                const nextSelectableColumns = (nextStrategy === 'mean' || nextStrategy === 'median' || nextStrategy === 'mode')
                  ? columns.filter((col) => numericColumns.includes(col))
                  : columns;
                setReplaceMissingColumns((prev) => prev.filter((col) => nextSelectableColumns.includes(col)));
              }}
              label="Strategy"
              sx={{ fontSize: 12 }}
            >
              {REPLACE_MISSING_STRATEGIES.map((strategy) => (
                <MenuItem key={strategy.value} value={strategy.value} sx={{ fontSize: 12 }}>
                  {strategy.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {replaceMissingStrategy === 'constant' && (
            <TextField
              size="small"
              label="Fill value"
              value={replaceMissingValue}
              onChange={(e) => setReplaceMissingValue(e.target.value)}
              placeholder="Leave empty for empty string on text columns; use NULL for SQL NULL"
              fullWidth
              sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
            />
          )}

          {replaceMissingStrategy !== 'constant' && (
            <Typography variant="caption" color="text.secondary">
              {replaceMissingStrategy === 'forward_fill'
                ? 'Forward fill uses the current row order to carry the last non-null value forward.'
                : 'Aggregate fill strategies use the selected column values to compute a replacement for null rows.'}
            </Typography>
          )}

          {replaceMissingNoOpDueToStats && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Selected columns have no NULL values in the current preview — fill is a no-op; SQL will not change.
            </Typography>
          )}

          <Button
            variant="contained"
            size="small"
            onClick={handleApplyReplaceMissing}
            disabled={!canApplyReplaceMissing}
            sx={{ fontSize: 12 }}
          >
            Apply Fill
          </Button>
        </Stack>
      </Popover>

      {/* ── Date Format Popover ─────────────────────────────────── */}
      <Popover
        open={!!dateFormatAnchor}
        anchorEl={dateFormatAnchor}
        onClose={() => setDateFormatAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 340 } }}
      >
        <TransformPopoverHeader title="Date Format" onClose={() => setDateFormatAnchor(null)} />

        <Stack spacing={1.5}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Column</InputLabel>
            <Select
              value={dateFormatColumn}
              onChange={(e) => setDateFormatColumn(e.target.value)}
              label="Column"
              sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}
            >
              {dateFormatSelectableColumns.map((column) => (
                <MenuItem key={column} value={column} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                  {column}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Output Format</InputLabel>
            <Select
              value={dateFormatPreset}
              onChange={(e) => setDateFormatPreset(e.target.value)}
              label="Output Format"
              sx={{ fontSize: 12 }}
            >
              {DATE_FORMAT_OPTIONS.map((format) => (
                <MenuItem key={format.value} value={format.value} sx={{ fontSize: 12 }}>
                  {format.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {dateFormatPreset === CUSTOM_DATE_FORMAT_VALUE && (
            <TextField
              size="small"
              label="Custom Format"
              value={dateFormatCustom}
              onChange={(e) => setDateFormatCustom(e.target.value)}
              placeholder="%d/%m/%Y"
              fullWidth
              sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
            />
          )}

          <TextField
            size="small"
            label="Input Format (optional)"
            value={dateInputFormat}
            onChange={(e) => setDateInputFormat(e.target.value)}
            placeholder="%Y-%m-%d"
            helperText="Use when parsing string-backed date columns with STRPTIME first."
            fullWidth
            sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={handleApplyDateFormat}
            disabled={!dateFormatColumn || (dateFormatPreset === CUSTOM_DATE_FORMAT_VALUE && !dateFormatCustom.trim())}
            sx={{ fontSize: 12 }}
          >
            Apply Date Format
          </Button>
        </Stack>
      </Popover>

      {/* ── Replace Popover ─────────────────────────────────────── */}
      <Popover
        open={!!replaceAnchor}
        anchorEl={replaceAnchor}
        onClose={() => setReplaceAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 340 } }}
      >
        <TransformPopoverHeader title="Replace Patterns" onClose={() => setReplaceAnchor(null)} />

        {replacePatternColumnsActive.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {replacePatternColumnsActive.map((column) => (
              <Chip
                key={`${column.alias}-${column.expression}`}
                label={formatReplacePatternChipLabel(column.alias, column.expression)}
                size="small"
                onDelete={() => handleRemoveAddedColumn(column.alias)}
                sx={{ fontSize: 11 }}
              />
            ))}
          </Stack>
        )}

        <Stack spacing={1.5}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Column</InputLabel>
            <Select
              value={replaceColumn}
              onChange={(e) => setReplaceColumn(e.target.value)}
              label="Column"
              sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}
            >
              {(replaceSelectableColumns.length > 0 ? replaceSelectableColumns : columns).map((col) => (
                <MenuItem key={col} value={col} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                  {col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label={replaceUseRegex ? 'Regex Pattern' : 'Find Value'}
            value={replaceFindValue}
            onChange={(e) => setReplaceFindValue(e.target.value)}
            placeholder={replaceUseRegex ? '(Yes|Y|true)' : 'SEVERE'}
            fullWidth
            sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
          />

          <TextField
            size="small"
            label="Replace With"
            value={replaceWithValue}
            onChange={(e) => setReplaceWithValue(e.target.value)}
            placeholder="Severe"
            fullWidth
            sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
            onKeyDown={(e) => { if (e.key === 'Enter' && replaceFindValue.trim()) handleApplyReplace(); }}
          />

          <Stack spacing={0.5}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={(
                <Checkbox
                  size="small"
                  checked={replaceUseRegex}
                  onChange={(e) => setReplaceUseRegex(e.target.checked)}
                />
              )}
              label="Use regex"
              componentsProps={{ typography: { fontSize: 12 } }}
            />
            <FormControlLabel
              sx={{ m: 0 }}
              control={(
                <Checkbox
                  size="small"
                  checked={replaceCaseSensitive}
                  onChange={(e) => setReplaceCaseSensitive(e.target.checked)}
                />
              )}
              label="Case-sensitive"
              componentsProps={{ typography: { fontSize: 12 } }}
            />
            <FormControlLabel
              sx={{ m: 0 }}
              control={(
                <Checkbox
                  size="small"
                  checked={replaceWholeWord}
                  onChange={(e) => setReplaceWholeWord(e.target.checked)}
                />
              )}
              label="Whole word match"
              componentsProps={{ typography: { fontSize: 12 } }}
            />
          </Stack>

          <Button
            variant="contained"
            size="small"
            onClick={handleApplyReplace}
            disabled={!replaceColumn || !replaceFindValue.trim()}
            sx={{ fontSize: 12 }}
          >
            Apply Replace
          </Button>
        </Stack>
      </Popover>

      {/* ── CASE / IF Popover ───────────────────────────────────── */}
      <Popover
        open={!!caseAnchor}
        anchorEl={caseAnchor}
        onClose={() => setCaseAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 420, maxWidth: 520 } }}
      >
        <TransformPopoverHeader title="CASE / IF" onClose={() => setCaseAnchor(null)} />

        {caseExpressions.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {caseExpressions.map((column) => (
              <Chip
                key={column.alias}
                label={column.alias}
                size="small"
                sx={{ fontSize: 11, alignSelf: 'flex-start' }}
              />
            ))}
          </Stack>
        )}

        <Stack spacing={1.5}>
          <TextField
            size="small"
            label="Output column name"
            required
            value={caseOutputName}
            onChange={(e) => setCaseOutputName(e.target.value)}
            placeholder="new_or_existing_column"
            fullWidth
            helperText="Use an existing column name to replace it"
            sx={{
              '& input': {
                fontSize: 12,
                fontFamily: '"Fira Code", monospace',
              },
              '& .MuiFormLabel-asterisk': {
                color: 'red',
              },
            }}
          />

          {caseClauses.map((clause, index) => {
            const needsCaseValue = FILTER_OPERATORS.find((operator) => operator.value === clause.operator)?.needsValue ?? true;
            return (
              <Stack key={index} spacing={1} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    WHEN #{index + 1}
                  </Typography>
                  {caseClauses.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => setCaseClauses((prev) => prev.filter((_, clauseIndex) => clauseIndex !== index))}
                      sx={{ p: 0.25 }}
                    >
                      <ClearIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Stack>

                <FormControl size="small" fullWidth>
                  <InputLabel sx={{ fontSize: 12 }}>Column</InputLabel>
                  <Select
                    value={clause.column}
                    onChange={(e) => setCaseClauses((prev) => prev.map((item, clauseIndex) => (
                      clauseIndex === index ? { ...item, column: e.target.value } : item
                    )))}
                    label="Column"
                    sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}
                  >
                    {columns.map((col) => (
                      <MenuItem key={col} value={col} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                        {col}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel sx={{ fontSize: 12 }}>Operator</InputLabel>
                  <Select
                    value={clause.operator}
                    onChange={(e) => setCaseClauses((prev) => prev.map((item, clauseIndex) => (
                      clauseIndex === index ? { ...item, operator: e.target.value as FilterOperator } : item
                    )))}
                    label="Operator"
                    sx={{ fontSize: 12 }}
                  >
                    {FILTER_OPERATORS.map((operator) => (
                      <MenuItem key={operator.value} value={operator.value} sx={{ fontSize: 12 }}>
                        {operator.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {needsCaseValue && (
                  <TextField
                    size="small"
                    label="Condition value"
                    value={clause.value}
                    onChange={(e) => setCaseClauses((prev) => prev.map((item, clauseIndex) => (
                      clauseIndex === index ? { ...item, value: e.target.value } : item
                    )))}
                    placeholder={clause.operator === 'IN' ? 'val1, val2, val3' : 'Enter value'}
                    fullWidth
                    sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
                  />
                )}

                <TextField
                  size="small"
                  label="THEN result"
                  value={clause.result}
                  onChange={(e) => setCaseClauses((prev) => prev.map((item, clauseIndex) => (
                    clauseIndex === index ? { ...item, result: e.target.value } : item
                  )))}
                  placeholder='Yes'
                  fullWidth
                  helperText="Use NULL for SQL NULL; numbers and booleans are kept unquoted"
                  sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
                />
              </Stack>
            );
          })}

          <Button
            size="small"
            variant="outlined"
            onClick={handleAddCaseClause}
            sx={{ alignSelf: 'flex-start', fontSize: 11 }}
          >
            + Add WHEN / THEN
          </Button>

          <TextField
            size="small"
            label="ELSE value"
            value={caseElseValue}
            onChange={(e) => setCaseElseValue(e.target.value)}
            placeholder="NULL"
            fullWidth
            helperText="Use multiple WHEN rows with the same THEN result to model OR-style logic"
            sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={handleApplyCase}
            disabled={!caseOutputName.trim() || !caseElseValue.trim() || caseClauses.length === 0 || caseHasInvalidClause}
            sx={{ fontSize: 12 }}
          >
            Apply CASE / IF
          </Button>
        </Stack>
      </Popover>

      {/* ── Rename Column Popover ──────────────────────────────── */}
      <Popover
        open={!!renameAnchor}
        anchorEl={renameAnchor}
        onClose={() => setRenameAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 320 } }}
      >
        <TransformPopoverHeader title="Rename Column" onClose={() => setRenameAnchor(null)} />

        {renamedColumns.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {renamedColumns.map((column) => (
              <Chip
                key={`${column.expression}->${column.alias}`}
                label={column.alias}
                size="small"
                sx={{ fontSize: 11, alignSelf: 'flex-start' }}
              />
            ))}
          </Stack>
        )}

        <Stack spacing={1.5}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Column</InputLabel>
            <Select
              value={renameColumn}
              onChange={(e) => {
                const value = e.target.value;
                setRenameColumn(value);
                setRenameNewName(value);
              }}
              label="Column"
              sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}
            >
              {columns.map((col) => (
                <MenuItem key={col} value={col} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                  {col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="New column name"
            value={renameNewName}
            onChange={(e) => setRenameNewName(e.target.value)}
            fullWidth
            error={!!renameError}
            helperText={renameError || ' '}
            sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleApplyRename(); }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={handleApplyRename}
            disabled={!renameColumn || !renameNewName.trim() || !!renameError}
            sx={{ fontSize: 12 }}
          >
            Rename Column
          </Button>
        </Stack>
      </Popover>

      {/* ── Cast Column Popover ────────────────────────────────── */}
      <Popover
        open={!!castAnchor}
        anchorEl={castAnchor}
        onClose={() => setCastAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 320 } }}
      >
        <TransformPopoverHeader title="Set Type / Cast" onClose={() => setCastAnchor(null)} />

        {castedColumns.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {castedColumns.map((column) => {
              const targetType = column.outputType
                || column.expression.match(/\s+AS\s+([A-Z]+)\)$/i)?.[1]
                || column.expression.match(/::([A-Z]+)\s*$/i)?.[1]
                || 'TYPE';
              return (
                <Chip
                  key={`${column.alias}->${targetType}`}
                  label={`${column.alias} -> ${targetType}`}
                  size="small"
                  sx={{ fontSize: 11, alignSelf: 'flex-start' }}
                />
              );
            })}
          </Stack>
        )}

        <Stack spacing={1.5}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Column</InputLabel>
            <Select
              value={castColumn}
              onChange={(e) => setCastColumn(e.target.value)}
              label="Column"
              sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}
            >
              {columns.map((col) => (
                <MenuItem key={col} value={col} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                  {col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Target Type</InputLabel>
            <Select
              value={castTargetType}
              onChange={(e) => setCastTargetType(e.target.value as (typeof CAST_TARGET_TYPES)[number])}
              label="Target Type"
              sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}
            >
              {CAST_TARGET_TYPES.map((targetType) => (
                <MenuItem key={targetType} value={targetType} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                  {targetType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            size="small"
            onClick={handleApplyCast}
            disabled={!castColumn || !castTargetType}
            sx={{ fontSize: 12 }}
          >
            Apply Cast
          </Button>
        </Stack>
      </Popover>

      {/* ── Drop Column Popover ─────────────────────────────────── */}
      <Popover
        open={!!dropAnchor}
        anchorEl={dropAnchor}
        onClose={() => {
          setDropAnchor(null);
          setDropColumns([]);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 320 } }}
      >
        <TransformPopoverHeader
          title="Drop Columns"
          onClose={() => {
            setDropAnchor(null);
            setDropColumns([]);
          }}
        />

        {transformState.excludeColumns.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {transformState.excludeColumns.map((column) => (
              <Chip
                key={column}
                label={column}
                size="small"
                sx={{ fontSize: 11, alignSelf: 'flex-start' }}
              />
            ))}
          </Stack>
        )}

        <Stack spacing={1.5}>
          <Autocomplete
            multiple
            size="small"
            options={columns}
            value={dropColumns}
            onChange={(_, value) => setDropColumns(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Columns to drop"
                placeholder={columns.length > 0 ? 'Select columns' : 'No columns available'}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  sx={{ fontSize: 10, fontFamily: '"Fira Code", monospace' }}
                />
              ))
            }
            sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={handleApplyDrop}
            disabled={dropColumns.length === 0}
            sx={{ fontSize: 12 }}
          >
            Drop Selected Columns
          </Button>
        </Stack>
      </Popover>

      {/* ── Split Popover ────────────────────────────────────────── */}
      <Popover
        open={!!splitAnchor}
        anchorEl={splitAnchor}
        onClose={() => setSplitAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 320 } }}
      >
        <TransformPopoverHeader title="Split Column" onClose={() => setSplitAnchor(null)} />

        <Stack spacing={1.5}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Column</InputLabel>
            <Select
              value={splitColumn}
              onChange={(e) => setSplitColumn(e.target.value)}
              label="Column"
              sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}
            >
              {(splitSelectableColumns.length > 0 ? splitSelectableColumns : columns).map((col) => (
                <MenuItem key={col} value={col} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                  {col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Delimiter"
            value={splitDelimiter}
            onChange={(e) => setSplitDelimiter(e.target.value)}
            placeholder=","
            fullWidth
            sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
          />

          <TextField
            size="small"
            label="Number of output columns"
            type="number"
            value={splitCount}
            onChange={(e) => setSplitCount(Math.max(2, Math.min(10, Number(e.target.value) || 2)))}
            inputProps={{ min: 2, max: 10 }}
            fullWidth
            sx={{ '& input': { fontSize: 12 } }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleApplySplit(); }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={handleApplySplit}
            disabled={!splitColumn || splitDelimiter === undefined}
            sx={{ fontSize: 12 }}
          >
            Apply Split
          </Button>
        </Stack>
      </Popover>

      {/* ── Window Popover ───────────────────────────────────────── */}
      <Popover
        open={!!windowAnchor}
        anchorEl={windowAnchor}
        onClose={() => setWindowAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, width: 420, maxWidth: 420 } }}
      >
        <TransformPopoverHeader title="Window Function" onClose={() => setWindowAnchor(null)} />

        {windowFunctions.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {windowFunctions.map((wf) => (
              <Chip
                key={wf.outputCol}
                label={`${wf.outputCol}: ${wf.func}`}
                size="small"
                onDelete={() => handleRemoveWindow(wf.outputCol)}
                sx={{ fontSize: 11 }}
              />
            ))}
          </Stack>
        )}

        <Stack spacing={1.5}>
          <TextField
            size="small"
            label="Output column name"
            value={windowOutputCol}
            onChange={(e) => setWindowOutputCol(e.target.value)}
            placeholder="event_rank"
            fullWidth
            sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
          />

          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Function</InputLabel>
            <Select
              value={windowFunc}
              onChange={(e) => setWindowFunc(e.target.value as WindowFunctionName)}
              label="Function"
              sx={{ fontSize: 12 }}
              MenuProps={{ PaperProps: { sx: { minWidth: 280 } } }}
            >
              <MenuItem value="ROW_NUMBER" sx={{ fontSize: 12 }}>ROW_NUMBER (ranking)</MenuItem>
              <MenuItem value="RANK" sx={{ fontSize: 12 }}>RANK (ranking)</MenuItem>
              <MenuItem value="DENSE_RANK" sx={{ fontSize: 12 }}>DENSE_RANK (ranking)</MenuItem>
              <MenuItem value="NTILE" sx={{ fontSize: 12 }}>NTILE (ranking)</MenuItem>
              <MenuItem value="RUNNING_SUM" sx={{ fontSize: 12 }}>RUNNING_SUM (aggregate)</MenuItem>
              <MenuItem value="RUNNING_COUNT" sx={{ fontSize: 12 }}>RUNNING_COUNT (aggregate)</MenuItem>
              <MenuItem value="SUM" sx={{ fontSize: 12 }}>SUM (aggregate)</MenuItem>
              <MenuItem value="AVG" sx={{ fontSize: 12 }}>AVG (aggregate)</MenuItem>
              <MenuItem value="COUNT" sx={{ fontSize: 12 }}>COUNT (aggregate)</MenuItem>
              <MenuItem value="MIN" sx={{ fontSize: 12 }}>MIN (aggregate)</MenuItem>
              <MenuItem value="MAX" sx={{ fontSize: 12 }}>MAX (aggregate)</MenuItem>
              <MenuItem value="LAG" sx={{ fontSize: 12 }}>LAG (navigation)</MenuItem>
              <MenuItem value="LEAD" sx={{ fontSize: 12 }}>LEAD (navigation)</MenuItem>
              <MenuItem value="FIRST_VALUE" sx={{ fontSize: 12 }}>FIRST_VALUE (navigation)</MenuItem>
              <MenuItem value="LAST_VALUE" sx={{ fontSize: 12 }}>LAST_VALUE (navigation)</MenuItem>
            </Select>
          </FormControl>

          {/* Fixed min-height so switching functions does not resize the popover. */}
          <Box sx={{ minHeight: 132, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {(needsValueForWindow.has(windowFunc) || valueColumnOptionalFunctions.has(windowFunc)) && (
              <FormControl size="small" fullWidth>
                <InputLabel sx={{ fontSize: 12 }}>
                  {windowFunc === 'COUNT' || windowFunc === 'RUNNING_COUNT' ? 'Value column (optional)' : 'Value column'}
                </InputLabel>
                <Select
                  value={windowValueCol}
                  onChange={(e) => setWindowValueCol(e.target.value)}
                  label={windowFunc === 'COUNT' || windowFunc === 'RUNNING_COUNT' ? 'Value column (optional)' : 'Value column'}
                  sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}
                >
                  {(windowFunc === 'COUNT' || windowFunc === 'RUNNING_COUNT') && (
                    <MenuItem value="" sx={{ fontSize: 12 }}><em>Use COUNT(*)</em></MenuItem>
                  )}
                  {columns.map((col) => (
                    <MenuItem key={col} value={col} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                      {col}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {windowFunc === 'NTILE' && (
              <TextField
                size="small"
                label="Buckets"
                type="number"
                value={windowNtileBuckets}
                onChange={(e) => setWindowNtileBuckets(e.target.value)}
                inputProps={{ min: 1, step: 1 }}
                fullWidth
                sx={{ '& input': { fontSize: 12 } }}
              />
            )}

            <Autocomplete
              multiple
              size="small"
              options={columns}
              value={windowPartitionBy}
              onChange={(_, val) => setWindowPartitionBy(val)}
              renderInput={(params) => <TextField {...params} label="Partition By (optional)" />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    sx={{ fontSize: 10, fontFamily: '"Fira Code", monospace' }}
                  />
                ))
              }
              sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
            />

            {(needsOrderForWindow.has(windowFunc) || aggregateFunctions.has(windowFunc)) && (
              <Stack direction="row" spacing={1}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel sx={{ fontSize: 12 }}>
                    {needsOrderForWindow.has(windowFunc) ? 'Order By (required)' : 'Order By (optional)'}
                  </InputLabel>
                  <Select
                    value={windowOrderCol}
                    onChange={(e) => setWindowOrderCol(e.target.value)}
                    label={needsOrderForWindow.has(windowFunc) ? 'Order By (required)' : 'Order By (optional)'}
                    sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}
                  >
                    {!needsOrderForWindow.has(windowFunc) && (
                      <MenuItem value="" sx={{ fontSize: 12 }}><em>None</em></MenuItem>
                    )}
                    {columns.map((col) => (
                      <MenuItem key={col} value={col} sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                        {col}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ width: 110 }}>
                  <InputLabel sx={{ fontSize: 12 }}>Direction</InputLabel>
                  <Select
                    value={windowOrderDir}
                    onChange={(e) => setWindowOrderDir(e.target.value as SortDir)}
                    label="Direction"
                    sx={{ fontSize: 12 }}
                    disabled={!windowOrderCol}
                  >
                    <MenuItem value="ASC" sx={{ fontSize: 12 }}>ASC</MenuItem>
                    <MenuItem value="DESC" sx={{ fontSize: 12 }}>DESC</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            )}
          </Box>

          <Button
            size="small"
            variant="text"
            onClick={() => setWindowAdvancedOpen((v) => !v)}
            sx={{ alignSelf: 'flex-start', fontSize: 11, textTransform: 'none', px: 0 }}
          >
            {windowAdvancedOpen ? 'Hide Advanced' : 'Advanced'}
          </Button>

          {windowAdvancedOpen && (
            <TextField
              size="small"
              label="Frame clause (ROWS BETWEEN ...)"
              value={windowFrame}
              onChange={(e) => setWindowFrame(e.target.value)}
              placeholder="UNBOUNDED PRECEDING AND CURRENT ROW"
              fullWidth
              sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
            />
          )}

          <Button
            variant="contained"
            size="small"
            onClick={handleApplyWindow}
            disabled={
              !windowOutputCol.trim()
              || (needsValueForWindow.has(windowFunc) && !windowValueCol)
              || (needsOrderForWindow.has(windowFunc) && !windowOrderCol)
              || !!windowTypeValidationError
              || !!ntileValidationError
            }
            sx={{ fontSize: 12 }}
          >
            Add Window Function
          </Button>

          {windowTypeValidationError && (
            <Typography variant="caption" color="error.main">
              {windowTypeValidationError}
            </Typography>
          )}
          {ntileValidationError && (
            <Typography variant="caption" color="error.main">
              {ntileValidationError}
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ minHeight: 56, display: 'block' }}>
            {rankingFunctions.has(windowFunc) && 'Ranking function: requires ORDER BY.'}
            {windowFunc === 'NTILE' && ' NTILE also requires a positive integer bucket count.'}
            {(windowFunc === 'RUNNING_SUM' || windowFunc === 'RUNNING_COUNT') && ' Running aggregate: requires ORDER BY and uses ROWS BETWEEN UNBOUNDED PRECEDING AND CURRENT ROW.'}
            {(windowFunc === 'SUM' || windowFunc === 'AVG' || windowFunc === 'MIN' || windowFunc === 'MAX' || windowFunc === 'COUNT') && ' Returns the same value on every row (global aggregate over the partition). Use RUNNING_SUM / RUNNING_COUNT for cumulative values. ORDER BY only applies when an explicit frame is set in Advanced.'}
            {aggregateFunctions.has(windowFunc) && windowFunc !== 'MIN' && windowFunc !== 'MAX' && windowFunc !== 'SUM' && windowFunc !== 'AVG' && windowFunc !== 'COUNT' && ' Aggregate window: value column required except COUNT(*) variants; ORDER BY is optional unless using RUNNING_*.'}
            {navigationFunctions.has(windowFunc) && 'Navigation function: requires value column and ORDER BY.'}
          </Typography>
        </Stack>
      </Popover>

      {/* ── Merge Popover ────────────────────────────────────────── */}
      <Popover
        open={!!mergeAnchor}
        anchorEl={mergeAnchor}
        onClose={() => setMergeAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 320 } }}
      >
        <TransformPopoverHeader title="Merge Columns" onClose={() => setMergeAnchor(null)} />

        <Stack spacing={1.5}>
          <Autocomplete
            multiple
            size="small"
            options={columns}
            value={mergeColumns}
            onChange={(_, val) => setMergeColumns(val)}
            renderInput={(params) => (
              <TextField {...params} label="Columns to merge" placeholder="Select 2+" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  sx={{ fontSize: 10, fontFamily: '"Fira Code", monospace' }}
                />
              ))
            }
            sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
          />

          <TextField
            size="small"
            label="Separator"
            value={mergeSeparator}
            onChange={(e) => setMergeSeparator(e.target.value)}
            placeholder=" "
            fullWidth
            sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
          />

          <TextField
            size="small"
            label="Output column name"
            value={mergeAlias}
            onChange={(e) => setMergeAlias(e.target.value)}
            placeholder="merged_column"
            fullWidth
            sx={{ '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace' } }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleApplyMerge(); }}
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={mergeDropSources}
                onChange={(e) => setMergeDropSources(e.target.checked)}
              />
            }
            label="Drop source columns"
            componentsProps={{ typography: { fontSize: 12 } }}
            sx={{ ml: -0.5 }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={handleApplyMerge}
            disabled={mergeColumns.length < 2 || !mergeAlias.trim()}
            sx={{ fontSize: 12 }}
          >
            Apply Merge
          </Button>
        </Stack>
      </Popover>
    </Box>
  );
};

export default TransformBar;
